import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import CollapsibleItinerarySelector from '../../components/CollapsibleItinerarySelector';
import InfoTip from '../../components/InfoTip';
import WhatsAppSendConfirmationDialog from '../../components/WhatsAppSendConfirmationDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Plus, 
  Users,
  User,
  UploadSimple,
  DownloadSimple,
  Check,
  X,
  Building,
  CaretDown,
  CaretRight,
  Clock,
  DoorOpen,
  Funnel,
  Info,
  Key,
  MagnifyingGlass,
  PencilSimple,
  DotsThree,
  MapPin,
  Trash,
  WarningCircle,
  Truck,
  WhatsappLogo
} from '@phosphor-icons/react';
import Bed from '../../components/icons/RoomBedIcon';
import api from '../../services/api';
import { toast } from 'sonner';
import { EventAwareDateRangePicker } from '../../components/EventAwareDatePicker';
import { cn } from '../../lib/utils';
import { getGuestsNeedingRoomCount } from './guestGroupFormUtils';

const arrivalFilters = [
  { value: 'all', label: 'All Guests' },
  { value: 'arriving_soon', label: 'Arriving Soon' },
  { value: 'reached_hotel', label: 'Reached Hotel' },
  { value: 'waiting_for_room', label: 'Waiting for Room' },
  { value: 'room_ready', label: 'Room Ready' },
  { value: 'keys_pending', label: 'Keys Pending' },
  { value: 'checked_in', label: 'Checked In' },
];

const journeyTone = {
  waiting_for_pickup: 'bg-slate-100 text-slate-700',
  pickup_assigned: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-cyan-100 text-cyan-700',
  reached_hotel: 'bg-amber-100 text-amber-700',
  waiting_for_room: 'bg-orange-100 text-orange-700',
  room_ready: 'bg-emerald-100 text-emerald-700',
  keys_handed_over: 'bg-violet-100 text-violet-700',
  checked_in: 'bg-green-100 text-green-700',
};

export default function RoomManagement() {
  const { user, currentEvent, isEventManager, isHotelManager, effectiveRole } = useAuth();
  const canCreateHotel = isEventManager || effectiveRole === 'host';
  const pendingHotelDropRef = useRef('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [stayPlans, setStayPlans] = useState([]);
  const [hotelProfiles, setHotelProfiles] = useState([]);
  const [eligibleGuestsByPlan, setEligibleGuestsByPlan] = useState({});
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arrivalsLoading, setArrivalsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'rooms');
  const [arrivalFilter, setArrivalFilter] = useState(searchParams.get('filter') || 'all');
  const [arrivalSearchQuery, setArrivalSearchQuery] = useState('');
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [journeyUpdating, setJourneyUpdating] = useState(false);
  const [showAddHotelDialog, setShowAddHotelDialog] = useState(false);
  const [showAddRoomDialog, setShowAddRoomDialog] = useState(false);
  const [showEditRoomDialog, setShowEditRoomDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showStayPlanDialog, setShowStayPlanDialog] = useState(false);
  const [showEditHotelDialog, setShowEditHotelDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [downloadingAssignments, setDownloadingAssignments] = useState(false);
  const [downloadingBulkTemplate, setDownloadingBulkTemplate] = useState(false);
  const [showAutoAllocationDialog, setShowAutoAllocationDialog] = useState(false);
  const [autoAllocationPreview, setAutoAllocationPreview] = useState(null);
  const [autoAllocationHotelName, setAutoAllocationHotelName] = useState('');
  const [previewingAutoAllocation, setPreviewingAutoAllocation] = useState(false);
  const [confirmingAutoAllocation, setConfirmingAutoAllocation] = useState(false);
  const [sendingRoomMessageScope, setSendingRoomMessageScope] = useState(null);
  const [pendingRoomMessage, setPendingRoomMessage] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState('invited');
  const [isAssignmentEligibilityOpen, setIsAssignmentEligibilityOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomToDelete, setRoomToDelete] = useState(null);
  const [hotelToDelete, setHotelToDelete] = useState('');
  const [stayPlanToDelete, setStayPlanToDelete] = useState(null);
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);
  const [assignmentStayPlanId, setAssignmentStayPlanId] = useState('');
  const [assigningGuests, setAssigningGuests] = useState(false);
  const [updatingRoom, setUpdatingRoom] = useState(false);
  const [deletingRoom, setDeletingRoom] = useState(false);
  const [deletingHotel, setDeletingHotel] = useState(false);
  const [deletingStayPlan, setDeletingStayPlan] = useState(false);
  const [guestSearchQuery, setGuestSearchQuery] = useState('');
  const [assignmentRoomNeedFilter, setAssignmentRoomNeedFilter] = useState('needs_room');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('unassigned');
  const [assignmentSideFilter, setAssignmentSideFilter] = useState('both');
  const [assignmentItineraryFilterIds, setAssignmentItineraryFilterIds] = useState([]);
  const [allowSplitOverride, setAllowSplitOverride] = useState(false);
  const [adHocGuest, setAdHocGuest] = useState({ name: '', phone: '', email: '' });
  const [showAdHocGuestForm, setShowAdHocGuestForm] = useState(false);
  const [creatingAdHocGuest, setCreatingAdHocGuest] = useState(false);
  const [collapsedHotelSections, setCollapsedHotelSections] = useState({});
  const [hotelRoomSearches, setHotelRoomSearches] = useState({});
  const [hotelToEdit, setHotelToEdit] = useState('');
  const [editedHotelName, setEditedHotelName] = useState('');
  const [hotelManagers, setHotelManagers] = useState([]);
  const [selectedStayPlanId, setSelectedStayPlanId] = useState(searchParams.get('stay_plan') || '');
  const [selectedHotelForStayPlan, setSelectedHotelForStayPlan] = useState('');
  const [editingStayPlanId, setEditingStayPlanId] = useState('');
  const [draggedItineraryId, setDraggedItineraryId] = useState('');
  const [editingCoverageDates, setEditingCoverageDates] = useState({});
  const [newStayPlan, setNewStayPlan] = useState({
    title: '',
    itinerary_event_ids: [],
    check_in_date: '',
    check_out_date: '',
    notes: ''
  });
  const [newHotel, setNewHotel] = useState({
    hotel_name: '',
    address: ''
  });
  const [newHotelItineraryIds, setNewHotelItineraryIds] = useState([]);
  const [newHotelStayDates, setNewHotelStayDates] = useState({ check_in_date: '', check_out_date: '' });
  const [editHotelItineraryIds, setEditHotelItineraryIds] = useState([]);
  const [editHotelStayDates, setEditHotelStayDates] = useState({ check_in_date: '', check_out_date: '' });
  const [hotelManagerForm, setHotelManagerForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [showHotelManagerFields, setShowHotelManagerFields] = useState(false);
  const [editingHotelManagerForm, setEditingHotelManagerForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [showEditHotelManagerFields, setShowEditHotelManagerFields] = useState(false);
  const [selectedHotelForRoom, setSelectedHotelForRoom] = useState('');
  const [newRoom, setNewRoom] = useState({
    room_number: '',
    floor: '',
    wing: '',
    capacity: 2
  });
  const selectedGuestIdsKey = selectedGuestIds.join('|');

  useEffect(() => {
    fetchData();
    fetchHotelManagers();
  }, [currentEvent?.id, isEventManager]);

  useEffect(() => {
    fetchArrivals();
  }, [currentEvent?.id, arrivalFilter]);

  useEffect(() => {
    setActiveTab(searchParams.get('tab') || 'rooms');
    setArrivalFilter(searchParams.get('filter') || 'all');
    setSelectedStayPlanId(searchParams.get('stay_plan') || '');
  }, [searchParams]);

  useEffect(() => {
    setAllowSplitOverride(false);
  }, [selectedRoom?.id, selectedGuestIdsKey]);

  useEffect(() => {
    if (showAssignDialog) return;
    setSelectedRoom(null);
    setSelectedGuestIds([]);
    setGuestSearchQuery('');
    setAssignmentRoomNeedFilter('needs_room');
    setAssignmentStatusFilter('unassigned');
    setAssignmentSideFilter('both');
    setAssignmentItineraryFilterIds([]);
    setAllowSplitOverride(false);
    setAssignmentStayPlanId('');
  }, [selectedStayPlanId, showAssignDialog]);

  useEffect(() => {
    if (!currentEvent?.id || !selectedStayPlanId) return;
    fetchEligibleStayPlanGuests(selectedStayPlanId);
  }, [currentEvent?.id, selectedStayPlanId]);

  const fetchData = async () => {
    if (!currentEvent?.id) return;
    try {
      const [roomsRes, guestsRes, stayPlansRes, rsvpsRes, hotelsRes] = await Promise.all([
        api.get(`/events/${currentEvent.id}/rooms`),
        api.get(`/events/${currentEvent.id}/guests`),
        api.get(`/events/${currentEvent.id}/stay-plans`).catch(() => ({ data: [] })),
        api.get(`/events/${currentEvent.id}/rsvp`).catch(() => ({ data: [] })),
        api.get(`/events/${currentEvent.id}/hotels`).catch(() => ({ data: [] }))
      ]);
      setRooms(roomsRes.data);
      setGuests(guestsRes.data);
      setStayPlans(stayPlansRes.data || []);
      setRsvps(rsvpsRes.data || []);
      setHotelProfiles(hotelsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleStayPlanGuests = async (stayPlanId) => {
    if (!currentEvent?.id || !stayPlanId) return;
    try {
      const response = await api.get(`/events/${currentEvent.id}/stay-plans/${stayPlanId}/eligible-guests`);
      setEligibleGuestsByPlan((prev) => ({
        ...prev,
        [stayPlanId]: response.data?.guests || []
      }));
    } catch (error) {
      console.error('Failed to fetch hotel eligible guests:', error);
    }
  };

  const fetchHotelManagers = async () => {
    if (!currentEvent?.id || !isEventManager) {
      setHotelManagers([]);
      return;
    }
    try {
      const response = await api.get(`/events/${currentEvent.id}/hotel-managers`);
      setHotelManagers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch hotel managers:', error);
    }
  };

  const fetchArrivals = async () => {
    if (!currentEvent?.id) return;
    try {
      setArrivalsLoading(true);
      const response = await api.get(`/events/${currentEvent.id}/hotel-arrivals`, {
        params: { filter: arrivalFilter }
      });
      setArrivals(response.data?.items || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch arrivals');
    } finally {
      setArrivalsLoading(false);
    }
  };

  const updateUrlState = (tab, filter = arrivalFilter) => {
    const next = {};
    if (tab) next.tab = tab;
    if (filter && filter !== 'all') next.filter = filter;
    if (selectedStayPlanId) next.stay_plan = selectedStayPlanId;
    setSearchParams(next);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    updateUrlState(value);
  };

  const handleArrivalFilterChange = (value) => {
    setArrivalFilter(value);
    updateUrlState('arrivals', value);
  };

  const selectStayPlan = (stayPlanId) => {
    setSelectedStayPlanId(stayPlanId || '');
    const next = {};
    if (activeTab) next.tab = activeTab;
    if (arrivalFilter && arrivalFilter !== 'all') next.filter = arrivalFilter;
    if (stayPlanId) next.stay_plan = stayPlanId;
    setSearchParams(next);
  };

  const generatePassword = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    return Array.from({ length: 10 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
  };

  const hasHotelManagerDraft = (form) => (
    Boolean(form.name?.trim() || form.email?.trim() || form.phone?.trim() || form.password?.trim())
  );

  const getApiErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.errors?.length) {
      return `${detail.message || fallback}: ${detail.errors.join(' ')}`;
    }
    return detail?.message || error.message || fallback;
  };

  const saveHotelManager = async (hotelName, form, existingManager = null) => {
    if (!isEventManager || !hasHotelManagerDraft(form)) return;

    const payload = {
      hotel_name: hotelName,
      name: form.name?.trim() || existingManager?.name || '',
      email: form.email?.trim() || null,
      phone: form.phone?.trim() || existingManager?.phone || '',
      password: form.password?.trim() || null
    };

    if (!payload.name || !payload.phone) {
      throw new Error('Hotel manager name and phone are required');
    }
    if (!existingManager && !payload.password) {
      throw new Error('Password is required for a new hotel manager');
    }

    await api.put(`/events/${currentEvent.id}/hotel-manager`, payload);
  };

  const handleAddHotel = async (e) => {
    e.preventDefault();
    const hotelName = (newHotel.hotel_name || '').trim();
    if (!hotelName) {
      toast.error('Hotel name is required');
      return;
    }
    if (isExistingHotelName(hotelName)) {
      toast.error(`${getCanonicalHotelName(hotelName)} already exists`);
      return;
    }
    const hasManagerDraft = hasHotelManagerDraft(hotelManagerForm);
    if (hasManagerDraft && (!hotelManagerForm.name?.trim() || !hotelManagerForm.phone?.trim() || !hotelManagerForm.password?.trim())) {
      toast.error('Complete name, phone, and password to add the optional hotel manager');
      return;
    }

    try {
      await api.post(`/events/${currentEvent.id}/hotels`, {
        hotel_name: hotelName,
        address: newHotel.address?.trim() || null,
        source: 'hotels_page'
      });
      if (hasManagerDraft) await saveHotelManager(hotelName, hotelManagerForm);
      await syncHotelItineraryTags(hotelName, newHotelItineraryIds, '', newHotelStayDates);
      toast.success(newHotelItineraryIds.length > 0 ? 'Hotel added and tagged successfully' : 'Hotel added successfully');
      setShowAddHotelDialog(false);
      setNewHotel({ hotel_name: '', address: '' });
      setNewHotelItineraryIds([]);
      setNewHotelStayDates({ check_in_date: '', check_out_date: '' });
      setHotelManagerForm({ name: '', email: '', phone: '', password: '' });
      setShowHotelManagerFields(false);
      await Promise.all([fetchData(), fetchHotelManagers()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to add hotel');
    }
  };

  const handleAddRoom = async (e) => {
    e.preventDefault();
    const canonicalHotelName = getCanonicalHotelName(selectedHotelForRoom);
    const roomNumber = (newRoom.room_number || '').trim();
    if (!canonicalHotelName || !roomNumber) {
      toast.error('Room number is required');
      return;
    }
    const roomExists = rooms.some(
      (room) =>
        normalizeValue(room.hotel_name) === normalizeValue(canonicalHotelName) &&
        normalizeValue(room.room_number) === normalizeValue(roomNumber)
    );

    if (roomExists) {
      toast.error(`Room ${roomNumber} already exists for hotel ${canonicalHotelName}`);
      return;
    }

    try {
      await api.post(`/events/${currentEvent.id}/rooms`, {
        ...newRoom,
        hotel_name: canonicalHotelName,
        room_number: roomNumber
      });
      toast.success('Room added successfully');
      setShowAddRoomDialog(false);
      setSelectedHotelForRoom('');
      setNewRoom({ room_number: '', floor: '', wing: '', capacity: 2 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to add room');
    }
  };

  const resetStayPlanForm = () => {
    setSelectedHotelForStayPlan('');
    setEditingStayPlanId('');
    setNewStayPlan({
      title: '',
      itinerary_event_ids: [],
      check_in_date: '',
      check_out_date: '',
      notes: ''
    });
  };

  const handleStayPlanItineraryToggle = (itineraryId, checked) => {
    setNewStayPlan((prev) => {
      const currentIds = prev.itinerary_event_ids || [];
      const nextIds = checked
        ? Array.from(new Set([...currentIds, itineraryId]))
        : currentIds.filter((itemId) => itemId !== itineraryId);
      const nextDates = getAutoStayDatesForItineraryIds(nextIds);
      return {
        ...prev,
        itinerary_event_ids: nextIds,
        check_in_date: nextDates.check_in_date || prev.check_in_date,
        check_out_date: nextDates.check_out_date || prev.check_out_date
      };
    });
  };

  const syncHotelItineraryTags = async (hotelName, itineraryIds = [], previousHotelName = '', stayDates = null) => {
    const canonicalHotelName = getCanonicalHotelName(hotelName) || getDisplayText(hotelName).trim();
    const uniqueIds = Array.from(new Set(itineraryIds.filter(Boolean)));
    if (!canonicalHotelName || uniqueIds.length === 0) return null;

    const autoDates = getAutoStayDatesForItineraryIds(uniqueIds);
    const checkInDate = autoDates.check_in_date || getDateKey(stayDates?.check_in_date);
    const checkOutDate = autoDates.check_out_date || getDateKey(stayDates?.check_out_date);
    if (!checkInDate || !checkOutDate) {
      throw new Error('Selected itinerary events must have dates before they can be tagged to a hotel');
    }
    if (checkOutDate <= checkInDate) {
      throw new Error('Hotel booking end date must be after the start date');
    }

    const candidatePlans = [
      ...getStayPlansForHotel(canonicalHotelName),
      ...getStayPlansForHotel(previousHotelName)
    ].filter(Boolean);
    const existingPlan = candidatePlans.find(
      (plan, index, list) => list.findIndex((item) => item.id === plan.id) === index
    );
    const payload = {
      title: `${canonicalHotelName} Event Days`,
      itinerary_event_ids: uniqueIds,
      check_in_date: checkInDate,
      check_out_date: checkOutDate,
      notes: null
    };

    if (existingPlan?.id) {
      return api.put(`/events/${currentEvent.id}/stay-plans/${existingPlan.id}`, payload);
    }

    return api.post(`/events/${currentEvent.id}/stay-plans`, {
      ...payload,
      hotel_name: canonicalHotelName
    });
  };

  const handleCreateStayPlan = async (e) => {
    e.preventDefault();
    const canonicalHotelName = getCanonicalHotelName(selectedHotelForStayPlan);
    if (!canonicalHotelName) {
      toast.error('Hotel is required');
      return;
    }
    if ((newStayPlan.itinerary_event_ids || []).length === 0) {
      toast.error('Select at least one event day');
      return;
    }
    const autoDates = getAutoStayDatesForItineraryIds(newStayPlan.itinerary_event_ids || []);
    if (!autoDates.check_in_date || !autoDates.check_out_date) {
      toast.error('Check-in and check-out dates are required');
      return;
    }
    if (stayPlanDraftHasInvalidRange) {
      toast.error('Check-out must be after check-in');
      return;
    }
    if (stayPlanDraftHasInvalidDates) {
      toast.error('Selected event days must fit inside the hotel dates');
      return;
    }

    try {
      const payload = {
        hotel_name: canonicalHotelName,
        title: newStayPlan.title || null,
        itinerary_event_ids: newStayPlan.itinerary_event_ids,
        check_in_date: autoDates.check_in_date,
        check_out_date: autoDates.check_out_date,
        notes: newStayPlan.notes || null
      };
      const response = editingStayPlanId
        ? await api.put(`/events/${currentEvent.id}/stay-plans/${editingStayPlanId}`, payload)
        : await api.post(`/events/${currentEvent.id}/stay-plans`, payload);
      toast.success(editingStayPlanId ? 'Tagged event days updated' : 'Hotel event days saved');
      setShowStayPlanDialog(false);
      resetStayPlanForm();
      selectStayPlan(response.data?.id || '');
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save hotel event days'));
    }
  };

  const handleAddItineraryToStayPlan = async (plan, itineraryId) => {
    if (!plan?.id || !itineraryId) return;
    if ((plan.itinerary_event_ids || []).includes(itineraryId)) {
      toast.info('This event day is already selected for this hotel');
      return;
    }

    const item = getItineraryById(itineraryId);
    const nextItineraryIds = [...(plan.itinerary_event_ids || []), itineraryId];
    const nextDates = getAutoStayDatesForItineraryIds(nextItineraryIds);

    try {
      await api.put(`/events/${currentEvent.id}/stay-plans/${plan.id}`, {
        itinerary_event_ids: nextItineraryIds,
        check_in_date: nextDates.check_in_date || getDateKey(plan.check_in_date),
        check_out_date: nextDates.check_out_date || getDateKey(plan.check_out_date)
      });
      toast.success(`${item?.name || 'Event day'} added to ${plan.hotel_name}`);
      await fetchData();
      await fetchEligibleStayPlanGuests(plan.id);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add event day'));
    }
  };

  const handleDropItineraryOnHotel = async (hotelName, itineraryId = draggedItineraryId) => {
    const item = getItineraryById(itineraryId);
    if (!item) return;
    const canonicalHotelName = getCanonicalHotelName(hotelName);
    if (!canonicalHotelName) {
      toast.error('Hotel is required');
      return;
    }

    const itemDateKey = getDateKey(item.date);

    if (!itemDateKey) {
      toast.error('This event day needs a date before it can be added to a hotel');
      setDraggedItineraryId('');
      return;
    }

    const dropKey = `${normalizeValue(canonicalHotelName)}:${itineraryId}`;
    if (pendingHotelDropRef.current === dropKey) {
      return;
    }
    pendingHotelDropRef.current = dropKey;

    try {
      const existingPlan = getStayPlansForHotel(canonicalHotelName)
        .find((plan) => (plan.itinerary_event_ids || []).includes(itineraryId));
      if (existingPlan) {
        toast.info(`${item?.name || 'Event day'} is already covered by ${canonicalHotelName}`);
        selectStayPlan(existingPlan.id);
        setDraggedItineraryId('');
        return;
      }

      const autoDates = getAutoStayDatesForItineraryIds([itineraryId]);
      const response = await api.post(`/events/${currentEvent.id}/stay-plans`, {
        hotel_name: canonicalHotelName,
      title: getDisplayText(item?.name) ? `${getDisplayText(item.name)} Stay` : `${canonicalHotelName} Event Days`,
        itinerary_event_ids: [itineraryId],
        check_in_date: autoDates.check_in_date,
        check_out_date: autoDates.check_out_date,
        notes: null
      });
      toast.success(`${item?.name || 'Event day'} added to ${canonicalHotelName}`);
      selectStayPlan(response.data?.id || '');
      await fetchData();
      if (response.data?.id) {
        await fetchEligibleStayPlanGuests(response.data.id);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to add event day to hotel'));
    } finally {
      pendingHotelDropRef.current = '';
    }
    setDraggedItineraryId('');
  };

  const handleDropItineraryOnStayPlan = async (plan, itineraryId = draggedItineraryId) => {
    if (!itineraryId) return;
    await handleAddItineraryToStayPlan(plan, itineraryId);
    setDraggedItineraryId('');
  };

  const startEditingCoverageDates = (plan) => {
    setEditingCoverageDates((prev) => ({
      ...prev,
      [plan.id]: {
        check_in_date: getDateKey(plan.check_in_date),
        check_out_date: getDateKey(plan.check_out_date)
      }
    }));
  };

  const updateCoverageDateDraft = (planId, field, value) => {
    setEditingCoverageDates((prev) => ({
      ...prev,
      [planId]: {
        ...(prev[planId] || {}),
        [field]: value
      }
    }));
  };

  const cancelEditingCoverageDates = (planId) => {
    setEditingCoverageDates((prev) => {
      const next = { ...prev };
      delete next[planId];
      return next;
    });
  };

  const saveCoverageDates = async (plan) => {
    const draft = editingCoverageDates[plan.id];
    if (!draft?.check_in_date || !draft?.check_out_date) {
      toast.error('Check-in and check-out dates are required');
      return;
    }
    if (draft.check_out_date <= draft.check_in_date) {
      toast.error('Check-out must be after check-in');
      return;
    }
    try {
      await api.put(`/events/${currentEvent.id}/stay-plans/${plan.id}`, {
        check_in_date: draft.check_in_date,
        check_out_date: draft.check_out_date
      });
      toast.success('Hotel dates updated');
      cancelEditingCoverageDates(plan.id);
      await fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update hotel dates'));
    }
  };

  const openEditRoomDialog = (room) => {
    setEditingRoom({
      id: room.id,
      hotel_name: getDisplayText(room.hotel_name),
      room_number: getDisplayText(room.room_number),
      floor: getDisplayText(room.floor),
      wing: getDisplayText(room.wing),
      capacity: room.capacity || 1
    });
    setShowEditRoomDialog(true);
  };

  const handleEditRoom = async (e) => {
    e.preventDefault();
    if (!editingRoom?.id) return;
    const roomNumber = (editingRoom.room_number || '').trim();
    if (!roomNumber) {
      toast.error('Room number is required');
      return;
    }

    try {
      setUpdatingRoom(true);
      await api.put(`/events/${currentEvent.id}/rooms/${editingRoom.id}`, {
        room_number: roomNumber,
        floor: editingRoom.floor || null,
        wing: editingRoom.wing || null,
        capacity: Number.parseInt(editingRoom.capacity, 10) || 1
      });
      toast.success('Room updated');
      setShowEditRoomDialog(false);
      setEditingRoom(null);
      await Promise.all([fetchData(), fetchArrivals()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update room');
    } finally {
      setUpdatingRoom(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!roomToDelete?.id) return;
    try {
      setDeletingRoom(true);
      await api.delete(`/events/${currentEvent.id}/rooms/${roomToDelete.id}`);
      toast.success('Room deleted');
      setRoomToDelete(null);
      await Promise.all([fetchData(), fetchArrivals()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    } finally {
      setDeletingRoom(false);
    }
  };

  const handleDeleteHotel = async () => {
    if (!hotelToDelete) return;
    try {
      setDeletingHotel(true);
      await api.delete(`/events/${currentEvent.id}/hotels`, {
        params: { hotel_name: hotelToDelete }
      });
      toast.success('Hotel deleted');
      if (selectedStayPlan && normalizeValue(selectedStayPlan.hotel_name) === normalizeValue(hotelToDelete)) {
        selectStayPlan('');
      }
      setHotelToDelete('');
      await Promise.all([fetchData(), fetchHotelManagers(), fetchArrivals()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete hotel');
    } finally {
      setDeletingHotel(false);
    }
  };

  const handleDeleteStayPlan = async () => {
    if (!stayPlanToDelete?.id) return;
    try {
      setDeletingStayPlan(true);
      await api.delete(`/events/${currentEvent.id}/stay-plans/${stayPlanToDelete.id}`);
      toast.success('Tagged event days deleted');
      if (selectedStayPlanId === stayPlanToDelete.id) {
        selectStayPlan('');
      }
      setStayPlanToDelete(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tagged event days');
    } finally {
      setDeletingStayPlan(false);
    }
  };

  const handleRemoveItineraryFromStayPlan = async (plan, itineraryId) => {
    if (!plan?.id || !itineraryId) return;
    const currentIds = plan.itinerary_event_ids || [];
    if (!currentIds.includes(itineraryId)) return;
    const remainingIds = currentIds.filter((itemId) => itemId !== itineraryId);
    if (remainingIds.length === 0) {
      setStayPlanToDelete(plan);
      return;
    }

    const nextDates = getAutoStayDatesForItineraryIds(remainingIds);
    try {
      await api.put(`/events/${currentEvent.id}/stay-plans/${plan.id}`, {
        itinerary_event_ids: remainingIds,
        check_in_date: nextDates.check_in_date || getDateKey(plan.check_in_date),
        check_out_date: nextDates.check_out_date || getDateKey(plan.check_out_date)
      });
      toast.success('Tagged event day removed');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove tagged event day');
    }
  };

  const handleBulkRoomUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(`/events/${currentEvent.id}/rooms/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data?.message || 'Rooms uploaded successfully');
      setShowBulkDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload rooms');
    } finally {
      e.target.value = '';
    }
  };

  const handleDownloadBulkTemplate = async () => {
    if (!currentEvent?.id) return;
    try {
      setDownloadingBulkTemplate(true);
      const response = await api.get(`/events/${currentEvent.id}/rooms/bulk-template`, {
        responseType: 'blob'
      });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `room_bulk_template_${currentEvent.id}.xlsx`;

      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloadingBulkTemplate(false);
    }
  };

  const handleDownloadAssignedDetails = async () => {
    if (!currentEvent?.id) return;
    try {
      setDownloadingAssignments(true);
      const response = await api.get(`/events/${currentEvent.id}/rooms/assignments/download`, {
        responseType: 'blob'
      });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `room_assignments_${currentEvent.id}.csv`;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Room assignment details downloaded');
    } catch (error) {
      toast.error('Failed to download room assignment details');
    } finally {
      setDownloadingAssignments(false);
    }
  };

  const handlePreviewAutoAllocation = async (hotelName = '') => {
    if (!currentEvent?.id) return;
    const canonicalHotelName = hotelName ? getCanonicalHotelName(hotelName) : '';
    try {
      setPreviewingAutoAllocation(canonicalHotelName ? getHotelSectionKey(canonicalHotelName) : 'all');
      const response = await api.post(
        `/events/${currentEvent.id}/rooms/auto-allocate/preview`,
        null,
        canonicalHotelName ? { params: { hotel_name: canonicalHotelName } } : undefined
      );
      setAutoAllocationHotelName(response.data?.hotel_name || canonicalHotelName);
      setAutoAllocationPreview(response.data);
      setShowAutoAllocationDialog(true);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to preview auto allocation'));
    } finally {
      setPreviewingAutoAllocation(false);
    }
  };

  const handleConfirmAutoAllocation = async () => {
    if (!currentEvent?.id) return;
    try {
      setConfirmingAutoAllocation(true);
      const response = await api.post(
        `/events/${currentEvent.id}/rooms/auto-allocate/confirm`,
        null,
        autoAllocationHotelName ? { params: { hotel_name: autoAllocationHotelName } } : undefined
      );
      setAutoAllocationPreview(response.data);
      toast.success(response.data?.message || 'Auto allocation completed');
      await Promise.all([fetchData(), fetchArrivals()]);
      setShowAutoAllocationDialog(false);
      setAutoAllocationHotelName('');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to confirm auto allocation'));
    } finally {
      setConfirmingAutoAllocation(false);
    }
  };

  const sendAccommodationUpdate = async (room = null) => {
    if (!currentEvent?.id) return;
    const guestIds = room
      ? getRoomAssignedGuestIds(room).filter(Boolean)
      : selectedStayPlan
        ? Array.from(new Set((selectedStayPlan.assignments || []).map((assignment) => assignment.guest_id).filter(Boolean)))
        : [];
    if (room && guestIds.length === 0) {
      toast.error('No guests assigned to this room');
      return;
    }
    if (!room && selectedStayPlan && guestIds.length === 0) {
      toast.error('No guests assigned for these hotel event days');
      return;
    }

    const scopeKey = room?.id || 'all';
    const formData = new FormData();
    formData.append('template_type', 'accommodation_update');
    if (room?.id) {
      formData.append('room_id', room.id);
    }
    if (activeRoomAllocationPlan?.id) {
      formData.append('stay_plan_id', activeRoomAllocationPlan.id);
    }
    if (room || selectedStayPlan) {
      formData.append('guest_ids', JSON.stringify(guestIds));
    }

    try {
      setSendingRoomMessageScope(scopeKey);
      const response = await api.post(
        `/events/${currentEvent.id}/whatsapp/send-template`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const summary = response.data?.summary || {};
      if (Number(summary.sent) > 0) {
        toast.success(`Room message sent to ${summary.sent} guest(s)`);
      } else if (Number(summary.sending) > 0) {
        toast.success(`Room message queued for ${summary.sending} guest(s)`);
      } else {
        toast.error('No room messages were sent');
      }
      if (summary.failed || summary.skipped) {
        toast.info(`${summary.failed || 0} failed, ${summary.skipped || 0} skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send room message');
    } finally {
      setSendingRoomMessageScope(null);
    }
  };

  const requestAccommodationUpdate = (room = null) => {
    const guestCount = room
      ? getRoomAssignedGuestIds(room).filter(Boolean).length
      : selectedStayPlan
        ? new Set((selectedStayPlan.assignments || []).map((assignment) => assignment.guest_id).filter(Boolean)).size
        : new Set(rooms.flatMap((item) => getRoomAssignedGuestIds(item).filter(Boolean))).size;
    setPendingRoomMessage({ room, guestCount });
  };

  const handleAssignGuest = async (forceAllowSplit = false) => {
    if (!selectedRoom || selectedGuestIds.length === 0) return;
    const targetStayPlan = assignmentStayPlan || selectedStayPlan;
    try {
      setAssigningGuests(true);
      const shouldAllowSplit = forceAllowSplit === true || allowSplitOverride || allocationExceeded;
      const assignUrl = targetStayPlan
        ? `/events/${currentEvent.id}/stay-plans/${targetStayPlan.id}/rooms/${selectedRoom.id}/assign/bulk`
        : `/events/${currentEvent.id}/rooms/${selectedRoom.id}/assign/bulk`;
      const response = await api.put(
        assignUrl,
        { guest_ids: selectedGuestIds, allow_split: shouldAllowSplit }
      );
      toast.success(response.data?.message || 'Guests assigned to room');
      setShowAssignDialog(false);
      setSelectedRoom(null);
      setSelectedGuestIds([]);
      setGuestSearchQuery('');
      setAllowSplitOverride(false);
      setAssignmentStayPlanId('');
      setAssignmentMode('invited');
      await Promise.all([fetchData(), fetchArrivals()]);
      if (targetStayPlan) {
        await fetchEligibleStayPlanGuests(targetStayPlan.id);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to assign guest'));
    } finally {
      setAssigningGuests(false);
    }
  };

  const handleCreateAdHocGuestAndAssign = async () => {
    const name = adHocGuest.name.trim();
    const phone = adHocGuest.phone.trim();
    const email = adHocGuest.email.trim();
    if (!selectedRoom || !name || !phone) {
      toast.error('Ad hoc guest name and phone are required');
      return;
    }
    if (selectedRoomAvailableBeds < 1) {
      toast.error('This room has no available beds');
      return;
    }

    try {
      setCreatingAdHocGuest(true);
      const guestResponse = await api.post(`/events/${currentEvent.id}/guests`, {
        name,
        phone,
        email: email || null,
        requires_room: true,
        tagged_itinerary_event_ids: []
      });
      await api.put(
        `/events/${currentEvent.id}/rooms/${selectedRoom.id}/assign/bulk`,
        { guest_ids: [guestResponse.data.id], allow_split: false }
      );
      toast.success('Ad hoc guest created and assigned');
      setAdHocGuest({ name: '', phone: '', email: '' });
      setShowAdHocGuestForm(false);
      setShowAssignDialog(false);
      setSelectedRoom(null);
      setSelectedGuestIds([]);
      setGuestSearchQuery('');
      setAllowSplitOverride(false);
      setAssignmentStayPlanId('');
      setAssignmentMode('invited');
      await Promise.all([fetchData(), fetchArrivals()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to create ad hoc guest');
    } finally {
      setCreatingAdHocGuest(false);
    }
  };

  const handleUnassignGuest = async (roomId, guestId, options = {}) => {
    try {
      const optionStayPlanIds = Array.isArray(options.stayPlanIds)
        ? options.stayPlanIds
        : options.stayPlanId
          ? [options.stayPlanId]
          : [];
      const targetStayPlanIds = optionStayPlanIds.length > 0
        ? optionStayPlanIds
        : selectedStayPlan?.id
          ? [selectedStayPlan.id]
          : [];
      const removeLegacyAssignment = options.removeLegacy !== undefined
        ? options.removeLegacy
        : targetStayPlanIds.length === 0;

      const requests = targetStayPlanIds.map((stayPlanId) => (
        api.put(`/events/${currentEvent.id}/stay-plans/${stayPlanId}/rooms/${roomId}/unassign?guest_id=${guestId}`)
      ));
      if (removeLegacyAssignment) {
        requests.push(api.put(`/events/${currentEvent.id}/rooms/${roomId}/unassign?guest_id=${guestId}`));
      }

      await Promise.all(requests);
      toast.success('Guest removed from room');
      await Promise.all([fetchData(), fetchArrivals()]);
      if (targetStayPlanIds.length > 0) {
        await Promise.all(targetStayPlanIds.map((stayPlanId) => fetchEligibleStayPlanGuests(stayPlanId)));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove guest');
    }
  };

  const handleJourneyUpdate = async (guestId, status) => {
    if (!currentEvent?.id || !guestId) return;
    try {
      setJourneyUpdating(true);
      await api.put(`/events/${currentEvent.id}/guests/${guestId}/journey`, { status });
      toast.success('Guest journey updated');
      await Promise.all([fetchData(), fetchArrivals()]);
      setSelectedArrival(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guest journey');
    } finally {
      setJourneyUpdating(false);
    }
  };

  const handleRoomOperation = async (roomId, payload) => {
    if (!currentEvent?.id || !roomId) return;
    try {
      setJourneyUpdating(true);
      await api.put(`/events/${currentEvent.id}/rooms/${roomId}/operations`, payload);
      toast.success('Room operations updated');
      await Promise.all([fetchData(), fetchArrivals()]);
      setSelectedArrival(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update room');
    } finally {
      setJourneyUpdating(false);
    }
  };

  const getRoomStatusSummary = (room) => {
    if (room.guest_checked_in) {
      return {
        label: 'Checked in',
        icon: Check,
        className: 'border-green-200 bg-green-50 text-green-700'
      };
    }
    if (room.keys_handed_over) {
      return {
        label: 'Keys handed',
        icon: Key,
        className: 'border-violet-200 bg-violet-50 text-violet-700'
      };
    }
    if (room.is_ready) {
      return {
        label: 'Room ready',
        icon: DoorOpen,
        className: 'border-emerald-200 bg-emerald-50 text-emerald-700'
      };
    }
    return {
      label: 'Pending',
      icon: Clock,
      className: 'border-amber-200 bg-amber-50 text-amber-700'
    };
  };

  const renderRoomStatusDropdown = (room) => {
    const status = getRoomStatusSummary(room);
    const StatusIcon = status.icon;
    const canManage = isEventManager || isHotelManager;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className={`h-7 w-10 gap-0.5 rounded-full px-1.5 ${status.className}`}
            aria-label={`Room status: ${status.label}`}
            title={`Room status: ${status.label}`}
          >
            <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
            <CaretDown className="h-3 w-3" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-xs text-muted-foreground">Room status</DropdownMenuLabel>
          <div className="px-2 pb-1 text-xs text-muted-foreground">
            <p>{room.is_ready ? 'Room ready' : 'Room pending'}</p>
            <p>{room.keys_handed_over ? 'Keys handed over' : 'Keys pending'}</p>
            <p>{room.guest_checked_in ? 'Guest checked in' : 'Check-in pending'}</p>
          </div>
          {canManage && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={room.is_ready || journeyUpdating}
                onSelect={() => handleRoomOperation(room.id, { is_ready: true })}
              >
                <DoorOpen className="h-4 w-4" /> Mark room ready
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!room.is_ready || room.keys_handed_over || journeyUpdating}
                onSelect={() => handleRoomOperation(room.id, { keys_handed_over: true })}
              >
                <Key className="h-4 w-4" /> Keys handed
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!room.keys_handed_over || room.guest_checked_in || journeyUpdating}
                onSelect={() => handleRoomOperation(room.id, { guest_checked_in: true })}
              >
                <Check className="h-4 w-4" /> Checked in
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const openAssignRoomForGuest = (guest) => {
    setSelectedArrival(null);
    setActiveTab('rooms');
    updateUrlState('rooms');
    setGuestSearchQuery(getDisplayText(guest.name));
    toast.info('Choose a room and use Add Guests. The guest name is prefilled in the assignment search.');
  };

  const getGuestById = (guestId) => guests.find(g => g.id === guestId);
  const guestRequiresRoom = (guest) => guest?.requires_room !== false;

  const getGuestPhoneKey = (guest) => getDisplayText(guest?.phone).trim() || `__no_phone_${guest?.id}`;

  const sortGuestGroupMembers = (items = []) => (
    [...items].sort((a, b) => {
      const primaryRank = (b?.is_primary_in_phone_group ? 1 : 0) - (a?.is_primary_in_phone_group ? 1 : 0);
      if (primaryRank !== 0) return primaryRank;
      const createdCompare = getDisplayText(a?.created_at).localeCompare(getDisplayText(b?.created_at));
      if (createdCompare !== 0) return createdCompare;
      const nameCompare = getDisplayText(a?.name).localeCompare(getDisplayText(b?.name));
      if (nameCompare !== 0) return nameCompare;
      return getDisplayText(a?.id).localeCompare(getDisplayText(b?.id));
    })
  );

  const guestGroupsByPhone = React.useMemo(() => {
    const groups = new Map();
    guests.forEach((guest) => {
      const key = getGuestPhoneKey(guest);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(guest);
    });
    return groups;
  }, [guests]);

  const getGuestPhoneGroupMembers = (guest) => {
    if (!guest) return [];
    const group = guestGroupsByPhone.get(getGuestPhoneKey(guest)) || [guest];
    return sortGuestGroupMembers(group);
  };

  const getGuestBookingOwner = (group = []) => {
    const roomMembers = sortGuestGroupMembers(group).filter(guestRequiresRoom);
    return roomMembers.find((item) => item.is_primary_in_phone_group) || roomMembers[0] || null;
  };

  const getGuestBookingMembers = (guest) => {
    if (!guest) return [];
    const group = getGuestPhoneGroupMembers(guest).filter(guestRequiresRoom);
    if (!group.some((item) => item.id === guest.id)) return [];
    const primary = getGuestBookingOwner(group);
    if (!primary || primary.id !== guest.id) return [guest];
    return [
      primary,
      ...group
        .filter((item) => item.id !== primary.id)
      .sort((a, b) => getDisplayText(a.name).localeCompare(getDisplayText(b.name)))
    ];
  };
  const getAssignmentFilterMembers = (guest) => {
    if (!guest) return [];
    const group = guestGroupsByPhone.get(getGuestPhoneKey(guest)) || [guest];
    return group.some((item) => item.id === guest.id) ? group : [guest];
  };

  const getGuestOccupantCount = (guest) => {
    const roomMembers = getGuestBookingMembers(guest);
    const companionCount = Number.parseInt(guest?.accompanying_guests || 0, 10);
    const bookingOwner = getGuestBookingOwner(getGuestPhoneGroupMembers(guest).filter(guestRequiresRoom));
    const namedPeople = bookingOwner?.id === guest?.id
      ? Math.max(roomMembers.length, 1)
      : 1;
    return namedPeople + (Number.isNaN(companionCount) ? 0 : Math.max(companionCount, 0));
  };

  function getDisplayText(value, fallback = '') {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (typeof value === 'object') {
      const keys = ['name', 'title', 'label', 'hall_name', 'room_number', 'value', 'id'];
      for (const key of keys) {
        if (value[key] !== value) {
          const text = getDisplayText(value[key], '');
          if (text) return text;
        }
      }
      return fallback;
    }
    return fallback;
  }

  const normalizeValue = (value) => getDisplayText(value).trim().toLowerCase();

  const filteredArrivals = arrivals.filter((item) => {
    const query = normalizeValue(arrivalSearchQuery);
    if (!query) return true;

    const guest = item.guest || {};
    const room = item.room || {};
    const searchableValues = [
      guest.name,
      guest.phone,
      guest.email,
      guest.side,
      guest.group_name,
      item.journey_label,
      item.journey_status,
      item.next_action,
      room.hotel_name,
      room.room_number,
      room.floor,
      room.wing
    ];

    return searchableValues.some((value) => normalizeValue(value).includes(query));
  });

  const selectedStayPlan = stayPlans.find((plan) => plan.id === selectedStayPlanId) || null;
  const assignmentStayPlan = stayPlans.find((plan) => plan.id === assignmentStayPlanId) || null;
  const activeRoomAllocationPlan = showAssignDialog
    ? assignmentStayPlan || selectedStayPlan
    : selectedStayPlan;
  const getDefaultStayPlanForHotel = (hotelName) => {
    const plans = stayPlans.filter((plan) => normalizeValue(plan.hotel_name) === normalizeValue(hotelName));
    return plans.find((plan) => (
      !['invalid', 'draft'].includes(plan?.readiness_status || 'draft') &&
      (plan.itinerary_event_ids || []).length > 0
    )) || plans[0] || null;
  };
  const getEligibleGuestIdsForPlan = (plan) => {
    if (!plan) return new Set();
    const eligibleGuests = eligibleGuestsByPlan[plan.id] || guests.filter((guest) => {
      const invitedIds = new Set(guest.tagged_itinerary_event_ids || []);
      return (
        guestRequiresRoom(guest) &&
        (plan.itinerary_event_ids || []).some((itemId) => invitedIds.has(itemId))
      );
    });
    return new Set(eligibleGuests.filter(guestRequiresRoom).map((guest) => guest.id));
  };
  const getHotelTaggedItineraryIds = (hotelName) => (
    Array.from(new Set(
      stayPlans
        .filter((plan) => normalizeValue(plan.hotel_name) === normalizeValue(hotelName))
        .flatMap((plan) => plan.itinerary_event_ids || [])
        .filter(Boolean)
    ))
  );
  const getRoomEligibilityItineraryIds = (room) => {
    return getHotelTaggedItineraryIds(room?.hotel_name);
  };
  const getGuestInvitedToItineraryIds = (guest, itineraryIds = []) => {
    if (!guest || itineraryIds.length === 0) return false;
    const invitedIds = new Set(guest.tagged_itinerary_event_ids || []);
    return itineraryIds.some((itemId) => invitedIds.has(itemId));
  };
  const getGuestGroupMembersForEvents = (guest) => {
    if (!guest) return [];
    const phoneKey = getGuestPhoneKey(guest);
    const group = guestGroupsByPhone.get(phoneKey) || [guest];
    return group.some((item) => item.id === guest.id) ? group : [guest];
  };
  const getGuestBookingEventLabel = (guest) => {
    const allItineraryItems = currentEvent?.itinerary || [];
    const allItineraryIds = allItineraryItems.map((item) => item.id).filter(Boolean);
    if (allItineraryIds.length === 0) return 'No events';

    const invitedIds = new Set(
      getGuestGroupMembersForEvents(guest)
        .flatMap((member) => member.tagged_itinerary_event_ids || [])
        .filter(Boolean)
    );

    if (allItineraryIds.every((itemId) => invitedIds.has(itemId))) {
      return 'All events';
    }

    const invitedNames = allItineraryItems
      .filter((item) => invitedIds.has(item.id))
      .map((item) => getDisplayText(item.name, 'Event day'))
      .filter(Boolean);

    return invitedNames.length > 0 ? invitedNames.join(' | ') : 'No events';
  };
  const guestHasDeclinedAnyItinerary = (guest, itineraryIds = []) => {
    if (!guest || itineraryIds.length === 0) return false;
    const group = guestGroupsByPhone.get(getGuestPhoneKey(guest)) || [guest];
    const groupGuestIds = new Set(group.map((item) => item.id));
    const targetIds = new Set(itineraryIds);
    return rsvps.some((rsvp) => (
      groupGuestIds.has(rsvp.guest_id) &&
      targetIds.has(rsvp.itinerary_item_id) &&
      rsvp.status === 'declined'
    ));
  };
  const getRoomInvitedGuestIds = (room) => {
    const itineraryIds = getRoomEligibilityItineraryIds(room);
    return new Set(
      guests
        .filter((guest) => (
          guestRequiresRoom(guest) &&
          getGuestInvitedToItineraryIds(guest, itineraryIds)
        ))
        .map((guest) => guest.id)
    );
  };
  const getRoomAssignableInvitedGuestIds = (room) => {
    const itineraryIds = getRoomEligibilityItineraryIds(room);
    return new Set(
      guests
        .filter((guest) => (
          guestRequiresRoom(guest) &&
          getGuestInvitedToItineraryIds(guest, itineraryIds) &&
          !guestHasDeclinedAnyItinerary(guest, itineraryIds)
        ))
        .map((guest) => guest.id)
    );
  };
  const selectedRoomHotelPlan = selectedRoom ? getDefaultStayPlanForHotel(selectedRoom.hotel_name) : null;
  const selectedRoomEligibleGuestIds = selectedRoom
    ? getRoomAssignableInvitedGuestIds(selectedRoom)
    : getEligibleGuestIdsForPlan(selectedRoomHotelPlan);
  const selectedRoomInvitedGuestIds = selectedRoom
    ? getRoomInvitedGuestIds(selectedRoom)
    : getEligibleGuestIdsForPlan(selectedRoomHotelPlan);

  const getAllocatedBedCount = (value) => {
    const parsed = Number.parseInt(value || 0, 10);
    return Number.isNaN(parsed) ? 0 : Math.max(parsed, 0);
  };

  const getLegacyRoomAssignedGuestIds = (room) => room?.assigned_guest_ids || [];

  const getLegacyRoomGuestAllocatedBeds = (room, guestId) => {
    const allocatedBeds = room?.assigned_guest_allocations?.[guestId];
    if (allocatedBeds !== undefined && allocatedBeds !== null) {
      const parsedBeds = Number.parseInt(allocatedBeds, 10);
      if (!Number.isNaN(parsedBeds)) return Math.max(parsedBeds, 0);
    }
    return getGuestOccupantCount(getGuestById(guestId));
  };

  const getStayPlanAssignmentRefsForRoom = (room, scopedPlan = activeRoomAllocationPlan) => {
    const plans = scopedPlan
      ? [scopedPlan]
      : stayPlans.filter((plan) => normalizeValue(plan.hotel_name) === normalizeValue(room?.hotel_name));
    return plans.flatMap((plan) => (
      (plan.assignments || [])
        .filter((assignment) => assignment.room_id === room?.id && assignment.guest_id)
        .map((assignment) => ({ plan, assignment }))
    ));
  };

  const stayPlanRangesOverlap = (left, right) => {
    const getLocalDateKey = (dateValue) => {
      if (!dateValue) return '';
      const stringValue = String(dateValue);
      if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
        return stringValue.slice(0, 10);
      }
      try {
        return new Date(dateValue).toISOString().slice(0, 10);
      } catch (_error) {
        return '';
      }
    };
    const leftStart = getLocalDateKey(left?.check_in_date);
    const leftEnd = getLocalDateKey(left?.check_out_date);
    const rightStart = getLocalDateKey(right?.check_in_date);
    const rightEnd = getLocalDateKey(right?.check_out_date);
    if (!leftStart || !leftEnd || !rightStart || !rightEnd) return false;
    return leftStart < rightEnd && rightStart < leftEnd;
  };

  const getStayPlanAssignmentLabel = (plan) => {
    const itineraryById = new Map((currentEvent?.itinerary || []).map((item) => [item.id, item]));
    const eventNames = (plan?.itinerary_event_ids || [])
      .map((itineraryId) => getDisplayText(itineraryById.get(itineraryId)?.name))
      .filter(Boolean);
    return eventNames.join(' + ') || getDisplayText(plan?.title, 'Event day');
  };

  const getRoomAssignmentRows = (room) => {
    const rowsByGuest = new Map();
    const rememberRow = (guestId, allocatedBeds, sourceLabel, sourceType, sourcePlanId = '') => {
      if (!guestId) return;
      if (!rowsByGuest.has(guestId)) {
        rowsByGuest.set(guestId, {
          guestId,
          allocatedBeds: 0,
          sourceLabels: [],
          sourcePlanIds: [],
          hasLegacyAssignment: false,
          hasStayPlanAssignment: false
        });
      }
      const row = rowsByGuest.get(guestId);
      row.allocatedBeds = Math.max(row.allocatedBeds, allocatedBeds);
      if (sourceLabel && !row.sourceLabels.includes(sourceLabel)) {
        row.sourceLabels.push(sourceLabel);
      }
      if (sourcePlanId && !row.sourcePlanIds.includes(sourcePlanId)) {
        row.sourcePlanIds.push(sourcePlanId);
      }
      if (sourceType === 'legacy') row.hasLegacyAssignment = true;
      if (sourceType === 'stay-plan') row.hasStayPlanAssignment = true;
    };

    if (!activeRoomAllocationPlan) {
      getLegacyRoomAssignedGuestIds(room).forEach((guestId) => {
        rememberRow(guestId, getLegacyRoomGuestAllocatedBeds(room, guestId), 'Room assignment', 'legacy');
      });
    }

    getStayPlanAssignmentRefsForRoom(room).forEach(({ plan, assignment }) => {
      rememberRow(
        assignment.guest_id,
        getAllocatedBedCount(assignment.allocated_beds),
        getStayPlanAssignmentLabel(plan),
        'stay-plan',
        plan.id
      );
    });

    return Array.from(rowsByGuest.values())
      .sort((a, b) => getDisplayText(getGuestById(a.guestId)?.name).localeCompare(getDisplayText(getGuestById(b.guestId)?.name)));
  };

  const getLegacyRoomOccupants = (room) => (
    getLegacyRoomAssignedGuestIds(room).reduce((sum, guestId) => (
      sum + getLegacyRoomGuestAllocatedBeds(room, guestId)
    ), 0)
  );

  const getRoomPeakStayPlanOccupancy = (room) => {
    const refs = getStayPlanAssignmentRefsForRoom(room, null);
    if (refs.length === 0) return 0;
    const plansForRoom = Array.from(
      new Map(refs.map(({ plan }) => [plan.id, plan])).values()
    );
    return plansForRoom.reduce((peak, targetPlan) => {
      const bedsByGuest = new Map();
      refs
        .filter(({ plan }) => plan.id === targetPlan.id || stayPlanRangesOverlap(plan, targetPlan))
        .forEach(({ assignment }) => {
          const guestId = assignment.guest_id || assignment.id;
          bedsByGuest.set(
            guestId,
            Math.max(bedsByGuest.get(guestId) || 0, getAllocatedBedCount(assignment.allocated_beds))
          );
        });
      const overlappingOccupancy = Array.from(bedsByGuest.values()).reduce((sum, beds) => sum + beds, 0);
      return Math.max(peak, overlappingOccupancy);
    }, 0);
  };

  const getRoomAllocationClashes = (room) => {
    const clashes = [];
    const capacity = room?.capacity || 0;
    const legacyOccupancy = getLegacyRoomOccupants(room);
    const stayPlanPeakOccupancy = getRoomPeakStayPlanOccupancy(room);
    const peakOccupancy = activeRoomAllocationPlan
      ? getStayPlanAssignmentRefsForRoom(room).reduce((sum, { assignment }) => (
          sum + getAllocatedBedCount(assignment.allocated_beds)
        ), 0)
      : Math.max(legacyOccupancy, stayPlanPeakOccupancy);

    if (capacity > 0 && peakOccupancy > capacity) {
      clashes.push(`Peak room occupancy is ${peakOccupancy}/${capacity} beds.`);
    }

    return Array.from(new Set(clashes));
  };

  const getRoomAssignedGuestIds = (room) => {
    return getRoomAssignmentRows(room).map((row) => row.guestId);
  };

  const getRoomGuestAllocatedBeds = (room, guestId) => {
    const row = getRoomAssignmentRows(room).find((item) => item.guestId === guestId);
    return row?.allocatedBeds || getGuestOccupantCount(getGuestById(guestId));
  };

  const getRoomNamedOccupantsForAssignment = (guest, allocatedBeds) => {
    const parsedAllocatedBeds = getAllocatedBedCount(allocatedBeds);
    const bookingMembers = getGuestBookingMembers(guest);
    const coveredNamedMembers = bookingMembers.slice(0, Math.min(parsedAllocatedBeds, bookingMembers.length));
    const namedOccupants = coveredNamedMembers.length > 0 ? coveredNamedMembers : [guest].filter(Boolean);
    return {
      namedOccupants,
      unnamedOccupantCount: Math.max(parsedAllocatedBeds - coveredNamedMembers.length, 0)
    };
  };

  const getRoomAssignedOccupants = (room) => {
    if (activeRoomAllocationPlan) {
      return getStayPlanAssignmentRefsForRoom(room).reduce((sum, { assignment }) => (
        sum + getAllocatedBedCount(assignment.allocated_beds)
      ), 0);
    }
    return Math.max(getLegacyRoomOccupants(room), getRoomPeakStayPlanOccupancy(room));
  };

  const getOverlappingStayPlanRoomOccupants = (room, scopedPlan = activeRoomAllocationPlan, excludedGuestIds = []) => {
    if (!room?.id || !scopedPlan) return 0;
    const excludedGuestIdSet = new Set(excludedGuestIds);
    const bedsByGuest = new Map();
    stayPlans
      .filter((plan) => (
        plan?.is_active !== false &&
        normalizeValue(plan.hotel_name) === normalizeValue(scopedPlan.hotel_name) &&
        (plan.id === scopedPlan.id || stayPlanRangesOverlap(scopedPlan, plan))
      ))
      .forEach((plan) => {
        (plan.assignments || [])
          .filter((assignment) => (
            assignment.room_id === room.id &&
            assignment.guest_id &&
            !excludedGuestIdSet.has(assignment.guest_id)
          ))
          .forEach((assignment) => {
            bedsByGuest.set(
              assignment.guest_id,
              Math.max(bedsByGuest.get(assignment.guest_id) || 0, getAllocatedBedCount(assignment.allocated_beds))
            );
          });
      });
    return Array.from(bedsByGuest.values()).reduce((sum, beds) => sum + beds, 0);
  };

  const getRoomAvailableBeds = (room) => Math.max((room?.capacity || 0) - getRoomAssignedOccupants(room), 0);

  const getGuestBookingAssignmentIds = (guest) => {
    if (!guest?.id) return [];
    const bookingMembers = getGuestBookingMembers(guest);
    return (bookingMembers.length > 0 ? bookingMembers : [guest])
      .map((member) => member?.id)
      .filter(Boolean);
  };

  const getGuestAllocatedBedsForSelection = (guest) => {
    if (!guest?.id) return 0;
    const assignmentIdSet = new Set(getGuestBookingAssignmentIds(guest));
    if (activeRoomAllocationPlan) {
      return (activeRoomAllocationPlan.assignments || []).reduce((sum, assignment) => (
        assignmentIdSet.has(assignment.guest_id)
          ? sum + getAllocatedBedCount(assignment.allocated_beds)
          : sum
      ), 0);
    }
    return rooms.reduce((sum, room) => (
      sum + getLegacyRoomAssignedGuestIds(room).reduce((roomSum, guestId) => (
        assignmentIdSet.has(guestId)
          ? roomSum + getLegacyRoomGuestAllocatedBeds(room, guestId)
          : roomSum
      ), 0)
    ), 0);
  };

  const getGuestRemainingBedCount = (guest) => Math.max(
    getGuestOccupantCount(guest) - getGuestAllocatedBedsForSelection(guest),
    0
  );

  const getGuestBedsRequiredForAssignment = (guest) => {
    const remainingBeds = getGuestRemainingBedCount(guest);
    return remainingBeds > 0 ? remainingBeds : getGuestOccupantCount(guest);
  };

  const getGuestAssignmentState = (guest) => {
    const occupantCount = getGuestOccupantCount(guest);
    const allocatedBedCount = getGuestAllocatedBedsForSelection(guest);
    if (allocatedBedCount >= occupantCount) return 'assigned';
    if (allocatedBedCount > 0) return 'partial';
    return 'unassigned';
  };

  const getGuestAssignedRoomLabels = (guest) => {
    if (!guest?.id) return [];
    const assignmentIdSet = new Set(getGuestBookingAssignmentIds(guest));
    if (activeRoomAllocationPlan) {
      const roomById = new Map(rooms.map((room) => [room.id, room]));
      return Array.from(new Set((activeRoomAllocationPlan.assignments || [])
        .filter((assignment) => assignmentIdSet.has(assignment.guest_id))
        .map((assignment) => {
          const room = roomById.get(assignment.room_id);
          return room ? `${getDisplayText(room.hotel_name, 'Hotel')} | Room ${getDisplayText(room.room_number)}` : '';
        })
        .filter(Boolean)));
    }
    return Array.from(new Set(rooms
      .filter((room) => getLegacyRoomAssignedGuestIds(room).some((guestId) => assignmentIdSet.has(guestId)))
      .map((room) => `${getDisplayText(room.hotel_name, 'Hotel')} | Room ${getDisplayText(room.room_number)}`)));
  };

  const getBookingPlacementSummary = (guest) => {
    const bookingMembers = getGuestBookingMembers(guest);
    const allocatedBedCount = getGuestAllocatedBedsForSelection(guest);
    const occupantCount = getGuestOccupantCount(guest);
    const remainingBedCount = Math.max(occupantCount - allocatedBedCount, 0);
    const coveredNamedMemberCount = Math.min(allocatedBedCount, bookingMembers.length);
    const uncoveredNamedMembers = bookingMembers.slice(coveredNamedMemberCount);
    return {
      bookingMembers,
      allocatedBedCount,
      occupantCount,
      remainingBedCount,
      uncoveredNamedMembers,
      unnamedRemainingBedCount: Math.max(remainingBedCount - uncoveredNamedMembers.length, 0)
    };
  };

  const getAssignmentGuestDisplay = (guest) => {
    const placement = getBookingPlacementSummary(guest);
    if (placement.allocatedBedCount > 0 && placement.remainingBedCount > 0) {
      if (placement.unnamedRemainingBedCount > 0) {
        return {
          name: `${placement.unnamedRemainingBedCount} unnamed ${placement.unnamedRemainingBedCount === 1 ? 'guest' : 'guests'}`,
          detail: `From ${getDisplayText(guest?.name, 'guest')} booking`
        };
      }
      return {
        name: `${placement.remainingBedCount} pending ${placement.remainingBedCount === 1 ? 'guest' : 'guests'}`,
        detail: `From ${getDisplayText(guest?.name, 'guest')} booking`
      };
    }
    return {
      name: getDisplayText(guest?.name, 'Guest'),
      detail: ''
    };
  };

  const getAssignableGuestBookings = () => {
    const rows = [];
    const handledPhoneKeys = new Set();
    const requiresRoomOnly = assignmentRoomNeedFilter === 'needs_room';
    guests.forEach((guest) => {
      if (requiresRoomOnly && !guestRequiresRoom(guest)) {
        return;
      }
      const phoneKey = getGuestPhoneKey(guest);
      const group = getGuestPhoneGroupMembers(guest)
        .filter((item) => !requiresRoomOnly || guestRequiresRoom(item));
      const primary = getGuestBookingOwner(group) || group.find((item) => item.is_primary_in_phone_group) || group[0];
      if (primary) {
        if (handledPhoneKeys.has(phoneKey)) {
          return;
        }
        handledPhoneKeys.add(phoneKey);
        if (!requiresRoomOnly) {
          rows.push(primary);
          return;
        }
        const placement = getBookingPlacementSummary(primary);
        if (placement.allocatedBedCount <= 0) {
          rows.push(primary);
          return;
        }
        if (placement.remainingBedCount <= 0) {
          return;
        }
        if (placement.unnamedRemainingBedCount > 0) {
          rows.push(primary);
          return;
        }
        placement.uncoveredNamedMembers.forEach((member) => rows.push(member));
        return;
      }

      if (getGuestAssignmentState(guest) !== 'assigned') {
        rows.push(guest);
      }
    });
    return rows;
  };

  const getHotelNames = () => {
    const hotelMap = new Map();
    const rememberHotel = (name) => {
      const trimmed = getDisplayText(name).trim();
      if (!trimmed) return;
      const key = normalizeValue(trimmed);
      if (!hotelMap.has(key)) hotelMap.set(key, trimmed);
    };

    rooms.forEach((room) => rememberHotel(room.hotel_name));
    stayPlans.forEach((plan) => rememberHotel(plan.hotel_name));
    hotelManagers.forEach((manager) => rememberHotel(manager.hotel_name));
    hotelProfiles.forEach((hotel) => rememberHotel(hotel.hotel_name));
    if (isHotelManager) rememberHotel(user?.hotel_manager_hotel_name);

    return Array.from(hotelMap.values()).sort((a, b) => a.localeCompare(b));
  };

  const assignableGuestBookings = getAssignableGuestBookings();
  const hotelTaggedItineraryIds = selectedRoom ? getRoomEligibilityItineraryIds(selectedRoom) : [];
  const hotelTaggedItineraryIdSet = new Set(hotelTaggedItineraryIds);
  const assignmentItineraryOptions = currentEvent?.itinerary || [];
  const filteredAssignmentGuests = assignableGuestBookings
    .filter((guest) => {
      const bookingMembers = getAssignmentFilterMembers(guest);
      const bookingItineraryIds = new Set(
        bookingMembers.flatMap((member) => member.tagged_itinerary_event_ids || [])
      );
      const assignmentState = getGuestAssignmentState(guest);
      const matchesAssignmentStatus = assignmentStatusFilter === 'both' ||
        (assignmentStatusFilter === 'assigned'
          ? assignmentState === 'assigned' || assignmentState === 'partial'
          : assignmentState === 'unassigned' || assignmentState === 'partial');
      const matchesSide = assignmentSideFilter === 'both' || (
        bookingMembers.some((member) => member.guest_side === assignmentSideFilter)
      );
      const matchesItinerary = (
        assignmentItineraryOptions.length > 0 &&
        assignmentItineraryFilterIds.length > 0 &&
        assignmentItineraryFilterIds.some((itemId) => bookingItineraryIds.has(itemId))
      );
      const query = normalizeValue(guestSearchQuery);
      const matchesSearch = !query || bookingMembers.some((member) => (
        normalizeValue(member.name).includes(query) ||
        normalizeValue(member.phone).includes(query)
      ));
      if (assignmentRoomNeedFilter === 'show_all') return matchesSearch;
      return matchesAssignmentStatus && matchesSide && matchesItinerary && matchesSearch;
    })
    .sort((left, right) => {
      if (assignmentStatusFilter === 'both') {
        const rank = { unassigned: 0, partial: 1, assigned: 2 };
        const leftRank = rank[getGuestAssignmentState(left)] ?? 0;
        const rightRank = rank[getGuestAssignmentState(right)] ?? 0;
        if (leftRank !== rightRank) return leftRank - rightRank;
      }
      return getDisplayText(left.name, 'Guest').localeCompare(getDisplayText(right.name, 'Guest'));
    });
  const hasDefaultAssignmentItineraryFilter = hotelTaggedItineraryIds.length === assignmentItineraryFilterIds.length &&
    hotelTaggedItineraryIds.every((itemId) => assignmentItineraryFilterIds.includes(itemId));
  const hasAssignmentGuestFilters = assignmentRoomNeedFilter !== 'needs_room' ||
    assignmentStatusFilter !== 'unassigned' ||
    assignmentSideFilter !== 'both' ||
    !hasDefaultAssignmentItineraryFilter;
  const assignmentSideLabel = assignmentSideFilter === 'both'
    ? 'Both sides'
    : assignmentSideFilter === 'bride'
      ? 'Bride side'
      : 'Groom side';
  const assignmentRoomNeedLabel = assignmentRoomNeedFilter === 'needs_room'
    ? 'Need room: Tagged'
    : assignmentRoomNeedFilter === 'show_all'
      ? 'Show all guests'
      : 'All matching guests';
  const assignmentStatusLabel = assignmentStatusFilter === 'unassigned'
    ? 'Room unassigned'
    : assignmentStatusFilter === 'assigned'
      ? 'Room assigned'
      : 'Both';
  const assignmentEventLabel = 'itinerary events';
  const assignmentEventSummary = assignmentItineraryOptions.length === 0
    ? `No ${assignmentEventLabel}`
    : assignmentItineraryFilterIds.length === assignmentItineraryOptions.length
      ? `All ${assignmentEventLabel} (${assignmentItineraryOptions.length})`
      : `${assignmentItineraryFilterIds.length} of ${assignmentItineraryOptions.length} events`;
  const toggleAssignmentItineraryFilter = (itineraryId, checked) => {
    setAssignmentRoomNeedFilter((current) => current === 'show_all' ? 'all' : current);
    setAssignmentItineraryFilterIds((currentIds) => (
      checked
        ? Array.from(new Set([...currentIds, itineraryId]))
        : currentIds.filter((itemId) => itemId !== itineraryId)
    ));
  };
  const handleShowAllAssignmentGuests = () => {
    setAssignmentRoomNeedFilter('show_all');
    setAssignmentStatusFilter('both');
    setAssignmentSideFilter('both');
    setAssignmentItineraryFilterIds(assignmentItineraryOptions.map((item) => item.id).filter(Boolean));
  };

  const groupRoomsByHotel = () => {
    const groupedHotelNames = selectedStayPlan ? [selectedStayPlan.hotel_name] : getHotelNames();
    const grouped = groupedHotelNames.reduce((acc, hotel) => {
      acc[hotel] = [];
      return acc;
    }, {});

    const visibleRooms = selectedStayPlan
      ? rooms.filter((room) => normalizeValue(room.hotel_name) === normalizeValue(selectedStayPlan.hotel_name))
      : rooms;

    return visibleRooms.reduce((acc, room) => {
      const hotel = room.hotel_name || 'Other';
      if (!acc[hotel]) acc[hotel] = [];
      acc[hotel].push(room);
      return acc;
    }, grouped);
  };

  const hotelNames = getHotelNames();
  const linkedStayItineraryIds = new Set(
    stayPlans.flatMap((plan) => plan.itinerary_event_ids || [])
  );
  const deferredStayEvents = (currentEvent?.itinerary || []).filter(
    (item) => item.needs_stay && !item.hotel_id && !linkedStayItineraryIds.has(item.id)
  );
  const getHotelProfile = (hotelName) => hotelProfiles.find(
    (hotel) => normalizeValue(hotel.hotel_name) === normalizeValue(hotelName)
  );
  const isExistingHotelName = (hotelName) => hotelNames.some(
    (hotel) => normalizeValue(hotel) === normalizeValue(hotelName)
  );

  const getCanonicalHotelName = (hotelName) => {
    const match = hotelNames.find((h) => normalizeValue(h) === normalizeValue(hotelName));
    return match || getDisplayText(hotelName).trim();
  };

  const getHotelSectionKey = (hotelName) => normalizeValue(hotelName || 'Other');

  const isHotelSectionCollapsed = (hotelName) => (
    collapsedHotelSections[getHotelSectionKey(hotelName)] === true
  );

  const toggleHotelSection = (hotelName) => {
    const sectionKey = getHotelSectionKey(hotelName);
    setCollapsedHotelSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const updateHotelRoomSearch = (hotelName, value) => {
    const sectionKey = getHotelSectionKey(hotelName);
    setHotelRoomSearches((prev) => ({
      ...prev,
      [sectionKey]: value
    }));
  };

  const getHotelGuestCount = (hotelRooms) => (
    hotelRooms.reduce((sum, room) => sum + getRoomAssignedOccupants(room), 0)
  );

  const getFilteredHotelRooms = (hotelRooms, searchQuery) => {
    const query = normalizeValue(searchQuery);

    return hotelRooms.filter((room) => {
      if (!query) return true;
      if (normalizeValue(room.room_number).includes(query)) return true;

      return getRoomAssignmentRows(room).some((assignmentRow) => {
        const guest = getGuestById(assignmentRow.guestId);
        const { namedOccupants } = getRoomNamedOccupantsForAssignment(guest, assignmentRow.allocatedBeds);
        return namedOccupants.some((member) => normalizeValue(member.name).includes(query));
      });
    });
  };

  const hotelGroups = groupRoomsByHotel();
  const hotelManagerByHotel = hotelManagers.reduce((acc, manager) => {
    acc[normalizeValue(manager.hotel_name)] = manager;
    return acc;
  }, {});
  const currentHotelManager = hotelManagerByHotel[normalizeValue(hotelToEdit)];
  const totalOccupancy = rooms.reduce((sum, r) => sum + getRoomAssignedOccupants(r), 0);
  const guestsNeedingRooms = getGuestsNeedingRoomCount(guests);
  const selectedGuests = selectedGuestIds
    .map((guestId) => getGuestById(guestId))
    .filter(Boolean);
  const selectedPeopleRequired = selectedGuests.reduce((sum, guest) => (
    sum + getGuestBedsRequiredForAssignment(guest)
  ), 0);
  const selectedFullyAssignedGuestIds = selectedGuests
    .filter((guest) => getGuestRemainingBedCount(guest) === 0)
    .map((guest) => guest.id);
  const selectedRoomOccupiedBeds = selectedRoom
    ? activeRoomAllocationPlan
      ? getOverlappingStayPlanRoomOccupants(selectedRoom, activeRoomAllocationPlan, selectedFullyAssignedGuestIds)
      : getRoomAssignedOccupants(selectedRoom)
    : 0;
  const selectedBedsAlreadyInRoom = selectedRoom
    ? getRoomAssignmentRows(selectedRoom).reduce((sum, row) => (
        !activeRoomAllocationPlan && selectedGuestIds.includes(row.guestId) && getGuestRemainingBedCount(getGuestById(row.guestId)) === 0
          ? sum + getAllocatedBedCount(row.allocatedBeds)
          : sum
      ), 0)
    : 0;
  const selectedRoomAvailableBeds = selectedRoom
    ? Math.max((selectedRoom.capacity || 0) - Math.max(selectedRoomOccupiedBeds - selectedBedsAlreadyInRoom, 0), 0)
    : 0;
  const remainingBedsAfterSelection = selectedRoomAvailableBeds - selectedPeopleRequired;
  const allocationExceeded = selectedGuestIds.length > 0 && remainingBedsAfterSelection < 0;
  const selectedHasCompanionGroup = selectedGuests.some((guest) => getGuestBedsRequiredForAssignment(guest) > 1);
  const allocationCandidateRooms = activeRoomAllocationPlan
    ? rooms.filter((room) => normalizeValue(room.hotel_name) === normalizeValue(activeRoomAllocationPlan.hotel_name))
    : rooms;
  const getRoomAvailableBedsForAssignment = (room) => {
    if (activeRoomAllocationPlan) {
      return Math.max(
        (room?.capacity || 0) - getOverlappingStayPlanRoomOccupants(room, activeRoomAllocationPlan, selectedFullyAssignedGuestIds),
        0
      );
    }
    return getRoomAvailableBeds(room);
  };
  const roomRecommendations = allocationExceeded
    ? allocationCandidateRooms
        .map((room) => {
          const availableBeds = getRoomAvailableBedsForAssignment(room);
          return {
            room,
            availableBeds,
            unusedBeds: availableBeds - selectedPeopleRequired,
            sameHotel: normalizeValue(room.hotel_name) === normalizeValue(selectedRoom?.hotel_name)
          };
        })
        .filter((item) => item.room.id !== selectedRoom?.id && item.availableBeds >= selectedPeopleRequired)
        .sort((a, b) => {
          if (a.unusedBeds !== b.unusedBeds) return a.unusedBeds - b.unusedBeds;
          if (a.sameHotel !== b.sameHotel) return a.sameHotel ? -1 : 1;
          return `${getDisplayText(a.room.hotel_name)} ${getDisplayText(a.room.room_number)}`.localeCompare(`${getDisplayText(b.room.hotel_name)} ${getDisplayText(b.room.room_number)}`);
        })
        .slice(0, 3)
    : [];
  const splitRoomSuggestionPlan = allocationExceeded
    ? allocationCandidateRooms
        .map((room) => ({ room, availableBeds: getRoomAvailableBedsForAssignment(room) }))
        .filter((item) => item.availableBeds > 0)
        .sort((a, b) => {
          if (b.availableBeds !== a.availableBeds) return b.availableBeds - a.availableBeds;
          return `${getDisplayText(a.room.hotel_name)} ${getDisplayText(a.room.room_number)}`.localeCompare(`${getDisplayText(b.room.hotel_name)} ${getDisplayText(b.room.room_number)}`);
        })
        .reduce((acc, item) => {
          if (acc.remaining <= 0) return acc;
          return {
            remaining: acc.remaining - item.availableBeds,
            rooms: [...acc.rooms, item]
          };
        }, { remaining: selectedPeopleRequired, rooms: [] })
    : { remaining: selectedPeopleRequired, rooms: [] };
  const splitRoomSuggestions = splitRoomSuggestionPlan.rooms;
  const canSplitAcrossRooms = allocationExceeded && splitRoomSuggestionPlan.remaining <= 0;
  const selectedOverCapacityBy = Math.abs(Math.min(remainingBedsAfterSelection, 0));
  const selectedBookingLabel = `${selectedGuestIds.length} booking${selectedGuestIds.length === 1 ? '' : 's'} selected | ${selectedPeopleRequired} ${selectedPeopleRequired === 1 ? 'person' : 'people'}`;
  const bestRoomRecommendation = roomRecommendations[0];

  const handleToggleGuestSelection = (guestId, checked) => {
    setSelectedGuestIds((prev) => {
      const alreadySelected = prev.includes(guestId);
      if (checked && !alreadySelected) {
        return [...prev, guestId];
      }
      if (!checked && alreadySelected) {
        return prev.filter((id) => id !== guestId);
      }
      return prev;
    });
  };

  const openRoomAssignmentDialog = async (room) => {
    const plan = getDefaultStayPlanForHotel(room.hotel_name);
    if (!plan || (plan.itinerary_event_ids || []).length === 0) {
      toast.error('Tag itinerary events to this hotel before adding guests');
      return;
    }
    if (!canAllocateStayPlan(plan)) {
      toast.error('Fix hotel event dates before adding guests');
      return;
    }
    setAssignmentStayPlanId(plan.id);
    if (!eligibleGuestsByPlan[plan.id]) {
      await fetchEligibleStayPlanGuests(plan.id);
    }

    setAssignmentMode('invited');
    setIsAssignmentEligibilityOpen(false);
    setSelectedRoom(room);
    setSelectedGuestIds([]);
    setGuestSearchQuery('');
    setAssignmentRoomNeedFilter('needs_room');
    setAssignmentStatusFilter('unassigned');
    setAssignmentSideFilter('both');
    setAssignmentItineraryFilterIds(getRoomEligibilityItineraryIds(room));
    setAdHocGuest({ name: '', phone: '', email: '' });
    setShowAdHocGuestForm(false);
    setAllowSplitOverride(false);
    setShowAssignDialog(true);
  };

  const handleSelectAllVisibleGuests = () => {
    setSelectedGuestIds((prev) => {
      const visibleGuestIds = filteredAssignmentGuests
        .map((guest) => guest.id);
      const nextToAdd = visibleGuestIds
        .filter((guestId) => !prev.includes(guestId));
      return [...prev, ...nextToAdd];
    });
  };

  const handleEditHotel = async (e) => {
    e.preventDefault();
    if (!hotelToEdit || !editedHotelName.trim()) return;

    try {
      await api.put(`/events/${currentEvent.id}/hotels/rename`, {
        current_name: hotelToEdit,
        new_name: editedHotelName.trim()
      });
      await saveHotelManager(editedHotelName.trim(), editingHotelManagerForm, currentHotelManager);
      await syncHotelItineraryTags(editedHotelName.trim(), editHotelItineraryIds, hotelToEdit, editHotelStayDates);
      toast.success('Hotel details updated');
      setShowEditHotelDialog(false);
      setHotelToEdit('');
      setEditedHotelName('');
      setEditHotelItineraryIds([]);
      setEditHotelStayDates({ check_in_date: '', check_out_date: '' });
      setEditingHotelManagerForm({ name: '', email: '', phone: '', password: '' });
      setShowEditHotelManagerFields(false);
      await Promise.all([fetchData(), fetchHotelManagers()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to update hotel details');
    }
  };

  const formatEta = (eta) => {
    if (!eta) return 'No ETA';
    try {
      return new Date(eta).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (_error) {
      return 'No ETA';
    }
  };

  const formatDateOnly = (dateValue) => {
    if (!dateValue) return 'Date not set';
    try {
      return new Date(dateValue).toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (_error) {
      return 'Date not set';
    }
  };

  const getDateKey = (dateValue) => {
    if (!dateValue) return '';
    const stringValue = String(dateValue);
    if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
      return stringValue.slice(0, 10);
    }
    try {
      return new Date(dateValue).toISOString().slice(0, 10);
    } catch (_error) {
      return '';
    }
  };

  const addDaysToDateKey = (dateKey, days) => {
    if (!dateKey) return '';
    const [year, month, day] = dateKey.split('-').map(Number);
    if (!year || !month || !day) return '';
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  };

  const getAutoStayDatesForItineraryIds = (itineraryIds = []) => {
    const dateKeys = itineraryIds
      .map((itineraryId) => getDateKey(getItineraryById(itineraryId)?.date))
      .filter(Boolean)
      .sort();
    if (dateKeys.length === 0) {
      return { check_in_date: '', check_out_date: '' };
    }
    const firstDate = dateKeys[0];
    const lastDate = dateKeys[dateKeys.length - 1];
    return {
      check_in_date: firstDate,
      check_out_date: addDaysToDateKey(lastDate, 1)
    };
  };

  const getStayDatesForSelectedEvents = (itineraryIds = [], fallbackDates = {}) => {
    const autoDates = getAutoStayDatesForItineraryIds(itineraryIds);
    return {
      check_in_date: autoDates.check_in_date || getDateKey(fallbackDates?.check_in_date),
      check_out_date: autoDates.check_out_date || getDateKey(fallbackDates?.check_out_date)
    };
  };

  function getItineraryById(itineraryId) {
    return (currentEvent?.itinerary || []).find((eventItem) => eventItem.id === itineraryId);
  }

  const getItineraryInvitedCount = (itineraryId) => (
    guests.filter((guest) => (guest.tagged_itinerary_event_ids || []).includes(itineraryId)).length
  );
  const getAllItineraryIds = () => (currentEvent?.itinerary || []).map((item) => item.id).filter(Boolean);

  const prefillStayPlanFromItinerary = (item, hotelName = '') => {
    const eventDateKey = getDateKey(item?.date);
    setEditingStayPlanId('');
    setSelectedHotelForStayPlan(hotelName || hotelNames[0] || '');
    setNewStayPlan({
      title: getDisplayText(item?.name) ? `${getDisplayText(item.name)} Stay` : '',
      itinerary_event_ids: item?.id ? [item.id] : [],
      check_in_date: eventDateKey,
      check_out_date: addDaysToDateKey(eventDateKey, 1),
      notes: ''
    });
    setShowStayPlanDialog(true);
  };

  const openStayPlanDialogForHotel = (hotelName, plan = null) => {
    const canonicalHotelName = getCanonicalHotelName(hotelName);
    setSelectedHotelForStayPlan(canonicalHotelName);
    if (plan?.id) {
      setEditingStayPlanId(plan.id);
      setNewStayPlan({
        title: getDisplayText(plan.title),
        itinerary_event_ids: plan.itinerary_event_ids || [],
        check_in_date: getDateKey(plan.check_in_date),
        check_out_date: getDateKey(plan.check_out_date),
        notes: getDisplayText(plan.notes)
      });
    } else {
      setEditingStayPlanId('');
      const uniqueIds = getAllItineraryIds();
      const nextDates = getAutoStayDatesForItineraryIds(uniqueIds);
      setNewStayPlan({
        title: canonicalHotelName ? `${canonicalHotelName} Event Days` : '',
        itinerary_event_ids: uniqueIds,
        check_in_date: nextDates.check_in_date,
        check_out_date: nextDates.check_out_date,
        notes: ''
      });
    }
    setShowStayPlanDialog(true);
  };

  const getStayPlanStatus = (plan) => plan?.readiness_status || 'draft';

  const canAllocateStayPlan = (plan) => (
    Boolean(plan) &&
    !['invalid', 'draft'].includes(getStayPlanStatus(plan)) &&
    (plan.validation_errors || []).length === 0
  );

  const getStayPlansForHotel = (hotelName) => (
    stayPlans
      .filter((plan) => normalizeValue(plan.hotel_name) === normalizeValue(hotelName))
      .sort((a, b) => new Date(a.check_in_date || 0) - new Date(b.check_in_date || 0))
  );

  const getAggregateStayDatesForHotel = (hotelName) => {
    const hotelStayPlans = getStayPlansForHotel(hotelName);
    const itineraryIds = Array.from(new Set(
      hotelStayPlans.flatMap((plan) => plan.itinerary_event_ids || [])
    ));
    const autoDates = getAutoStayDatesForItineraryIds(itineraryIds);
    if (autoDates.check_in_date && autoDates.check_out_date) {
      return autoDates;
    }

    const dateRanges = hotelStayPlans
      .map((plan) => ({
        check_in_date: getDateKey(plan.check_in_date),
        check_out_date: getDateKey(plan.check_out_date)
      }))
      .filter((range) => range.check_in_date && range.check_out_date);
    if (dateRanges.length === 0) {
      return { check_in_date: '', check_out_date: '' };
    }

    return {
      check_in_date: dateRanges.map((range) => range.check_in_date).sort()[0],
      check_out_date: dateRanges.map((range) => range.check_out_date).sort().slice(-1)[0]
    };
  };

  const getItineraryName = (itineraryId) => {
    const item = (currentEvent?.itinerary || []).find((eventItem) => eventItem.id === itineraryId);
    return getDisplayText(item?.name, 'Event day');
  };

  const getStayPlanTitle = (plan) => (
    getDisplayText(plan?.title) || (plan?.itinerary_event_ids || []).map(getItineraryName).join(' + ') || 'Hotel Event Days'
  );

  const getSelectedItinerarySummary = (itineraryIds = []) => {
    if (!itineraryIds.length) return 'No events selected';
    const selectedItems = itineraryIds.map(getItineraryById).filter(Boolean);
    const names = selectedItems
      .map((item) => getDisplayText(item.name, 'Event day'))
      .filter(Boolean);
    if (itineraryIds.length === (currentEvent?.itinerary || []).length) {
      return `All ${itineraryIds.length} events selected`;
    }
    return names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2} more` : '');
  };

  const getStayDateSummary = (stayDates = {}) => {
    const checkIn = getDateKey(stayDates.check_in_date);
    const checkOut = getDateKey(stayDates.check_out_date);
    if (!checkIn || !checkOut) return '';
    return `${formatDateOnly(checkIn)} to ${formatDateOnly(checkOut)}`;
  };

  const getDraftItineraryDateValidation = (item) => {
    const eventDateKey = getDateKey(item.date);
    const checkInKey = getDateKey(newStayPlan.check_in_date);
    const checkOutKey = getDateKey(newStayPlan.check_out_date);
    const isSelected = (newStayPlan.itinerary_event_ids || []).includes(item.id);
    const canCheck = isSelected && eventDateKey && checkInKey && checkOutKey;
    const isWithin = canCheck && eventDateKey >= checkInKey && eventDateKey < checkOutKey;
    return { canCheck, isWithin };
  };

  const stayPlanDraftHasInvalidDates = (currentEvent?.itinerary || []).some((item) => {
    const validation = getDraftItineraryDateValidation(item);
    return validation.canCheck && !validation.isWithin;
  });

  const stayPlanDraftHasInvalidRange = (
    newStayPlan.check_in_date &&
    newStayPlan.check_out_date &&
    getDateKey(newStayPlan.check_out_date) <= getDateKey(newStayPlan.check_in_date)
  );

  const selectedGuest = selectedArrival?.guest;
  const selectedRoomForArrival = selectedArrival?.room;
  const selectedJourney = selectedArrival?.journey_status;

  const renderJourneyActions = () => {
    if (!selectedGuest) return null;
    const guestId = selectedGuest.id;
    const roomId = selectedRoomForArrival?.id;
    return (
      <div className="grid gap-2">
        {['waiting_for_pickup', 'pickup_assigned'].includes(selectedJourney) && (
          <Button
            onClick={() => handleJourneyUpdate(guestId, 'picked_up')}
            disabled={journeyUpdating}
            className="justify-start bg-wedding-sage hover:bg-wedding-sage/90"
          >
            <Truck className="w-4 h-4 mr-2" /> Mark Guest Picked Up
          </Button>
        )}
        {selectedJourney === 'picked_up' && (
          <Button
            onClick={() => handleJourneyUpdate(guestId, 'reached_hotel')}
            disabled={journeyUpdating}
            className="justify-start bg-wedding-sage hover:bg-wedding-sage/90"
          >
            <MapPin className="w-4 h-4 mr-2" /> Mark Reached Hotel
          </Button>
        )}
        {['reached_hotel', 'waiting_for_room'].includes(selectedJourney) && !roomId && (
          <Button
            onClick={() => openAssignRoomForGuest(selectedGuest)}
            disabled={journeyUpdating}
            className="justify-start bg-wedding-sage hover:bg-wedding-sage/90"
          >
            <Bed className="w-4 h-4 mr-2" /> Assign Room
          </Button>
        )}
        {['reached_hotel', 'waiting_for_room'].includes(selectedJourney) && roomId && (
          <Button
            onClick={() => handleRoomOperation(roomId, { is_ready: true })}
            disabled={journeyUpdating}
            className="justify-start bg-wedding-sage hover:bg-wedding-sage/90"
          >
            <DoorOpen className="w-4 h-4 mr-2" /> Mark Room Ready
          </Button>
        )}
        {selectedJourney === 'room_ready' && roomId && (
          <Button
            onClick={() => handleRoomOperation(roomId, { keys_handed_over: true })}
            disabled={journeyUpdating}
            className="justify-start bg-wedding-sage hover:bg-wedding-sage/90"
          >
            <Key className="w-4 h-4 mr-2" /> Mark Keys Handed Over
          </Button>
        )}
        {selectedJourney === 'keys_handed_over' && roomId && (
          <Button
            onClick={() => handleRoomOperation(roomId, { guest_checked_in: true })}
            disabled={journeyUpdating}
            className="justify-start bg-wedding-sage hover:bg-wedding-sage/90"
          >
            <Check className="w-4 h-4 mr-2" /> Mark Checked In
          </Button>
        )}
        {roomId && selectedJourney !== 'checked_in' && (
          <Button
            variant="outline"
            onClick={() => openAssignRoomForGuest(selectedGuest)}
            disabled={journeyUpdating}
            className="justify-start"
          >
            <Bed className="w-4 h-4 mr-2" /> Change Room
          </Button>
        )}
        {selectedJourney === 'checked_in' && (
          <p className="rounded-md bg-green-50 p-3 text-sm text-green-700">
            Guest journey is complete.
          </p>
        )}
      </div>
    );
  };

  const renderHotelItineraryPicker = (selectedIds, setter, idPrefix, stayDates, setStayDates, defaultSelectAll = true) => {
    const itineraryItems = currentEvent?.itinerary || [];
    if (itineraryItems.length === 0) {
      return (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          Add itinerary event days first, then tag this hotel to the days it covers.
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <CollapsibleItinerarySelector
          items={itineraryItems}
          selectedIds={selectedIds}
          onChange={(nextIds) => {
            setter(nextIds);
            if (setStayDates) setStayDates((current) => getStayDatesForSelectedEvents(nextIds, current));
          }}
          title="Tagged Itinerary Events"
          description="Guests invited to these event days become eligible for this hotel's room allocation."
          emptyMessage="Add itinerary event days first, then tag this hotel to the days it covers."
          idPrefix={idPrefix}
          defaultSelectAll={defaultSelectAll}
          summary={`${getSelectedItinerarySummary(selectedIds)}${getStayDateSummary(stayDates) ? ` | ${getStayDateSummary(stayDates)}` : ''}`}
          renderPrimaryLabel={(item) => getDisplayText(item.name, 'Event day')}
          renderSecondaryLabel={(item) => `${formatDateOnly(item.date)} | ${getItineraryInvitedCount(item.id)} invited`}
        />
        {selectedIds.length > 0 && (
          <div className="rounded-md bg-emerald-50 px-3 py-2">
            <EventAwareDateRangePicker
              id={`${idPrefix}-stay-dates`}
              label="Hotel booking window"
              startLabel="Start"
              endLabel="End"
              startValue={stayDates?.check_in_date || ''}
              endValue={stayDates?.check_out_date || ''}
              onStartChange={(date) => setStayDates((current) => ({ ...(current || {}), check_in_date: date }))}
              onEndChange={(date) => setStayDates((current) => ({ ...(current || {}), check_out_date: date }))}
              eventStartDate={currentEvent?.start_date}
              eventEndDate={currentEvent?.end_date}
              minimumNights={1}
              required
              className="space-y-1"
              triggerClassName="min-h-9 py-1.5"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <StickyActionHeader>
          <div className="flex items-center gap-1">
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="rooms-title">Hotel Allocation</h1>
            <InfoTip content="Manage hotels, rooms, guest allocations, arrivals, and key handovers." label="About hotel allocation" />
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end" data-header-action-group>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  aria-label="More room actions"
                  title="More room actions"
                  data-header-action
                  data-testid="room-actions-menu-btn"
                  className="h-10 min-w-10 px-3"
                >
                  <DotsThree className="sm:mr-2" style={{ width: 24, height: 24 }} weight="bold" />
                  <span className="hidden sm:inline">More</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-muted-foreground">Room actions</DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={handleDownloadAssignedDetails}
                  disabled={downloadingAssignments}
                  data-testid="download-room-assignments-btn"
                >
                  <DownloadSimple className="h-4 w-4" />
                  {downloadingAssignments ? 'Downloading...' : 'Download Assigned'}
                </DropdownMenuItem>
                {isEventManager && (
                  <DropdownMenuItem
                    onSelect={() => requestAccommodationUpdate()}
                    disabled={sendingRoomMessageScope === 'all' || totalOccupancy === 0}
                    data-testid="send-all-room-messages-btn"
                  >
                    <WhatsappLogo className="h-4 w-4" />
                    {sendingRoomMessageScope === 'all' ? 'Sending...' : 'Send Room Updates'}
                  </DropdownMenuItem>
                )}
                {(isEventManager || isHotelManager) && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(event) => {
                        event.preventDefault();
                        setShowBulkDialog(true);
                      }}
                      data-testid="bulk-room-allocation-btn"
                    >
                      <UploadSimple className="h-4 w-4" />
                      Bulk Room Allocation
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {(isEventManager || isHotelManager) && (
              <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Bulk Room Allocation</DialogTitle>
                    <DialogDescription>
                      Use one template to add rooms and assign guests in one upload.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="rounded-md border p-3 space-y-2">
                      <p className="text-sm font-medium">Step 1: Download template</p>
                      <p className="text-sm text-muted-foreground">
                        Template includes guest dropdown values from your current guest list.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleDownloadBulkTemplate}
                        disabled={downloadingBulkTemplate}
                        data-testid="download-bulk-room-template-btn"
                      >
                        <DownloadSimple className="w-4 h-4 mr-2" />
                        {downloadingBulkTemplate ? 'Downloading...' : 'Download Template'}
                      </Button>
                    </div>
                    <div className="rounded-md border p-3 space-y-2">
                      <p className="text-sm font-medium">Step 2: Upload completed template</p>
                      <Input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleBulkRoomUpload}
                        data-testid="bulk-room-upload-input"
                      />
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}

              {canCreateHotel && (
                <Dialog
                  open={showAddHotelDialog}
                  onOpenChange={(open) => {
                    setShowAddHotelDialog(open);
                    if (open) {
                      const allItineraryIds = getAllItineraryIds();
                      setNewHotelItineraryIds(allItineraryIds);
                      setNewHotelStayDates(getAutoStayDatesForItineraryIds(allItineraryIds));
                      setShowHotelManagerFields(false);
                    } else {
                      setNewHotel({ hotel_name: '', address: '' });
                      setNewHotelItineraryIds([]);
                      setNewHotelStayDates({ check_in_date: '', check_out_date: '' });
                      setHotelManagerForm({ name: '', email: '', phone: '', password: '' });
                      setShowHotelManagerFields(false);
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button
                      className="bg-wedding-sage hover:bg-wedding-sage/90"
                      aria-label="Add hotel"
                      title="Add hotel"
                      data-header-action
                      data-testid="add-hotel-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <span className="truncate">Add Hotel</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add Hotel</DialogTitle>
                      <DialogDescription>Create the hotel, optionally link event days, and add manager access if needed.</DialogDescription>
                      </DialogHeader>
                    <form onSubmit={handleAddHotel} className="space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="new_hotel_name">Hotel Name *</Label>
                          <Input
                            id="new_hotel_name"
                            value={newHotel.hotel_name}
                            onChange={(e) => setNewHotel({ ...newHotel, hotel_name: e.target.value })}
                            required
                            data-testid="hotel-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="new_hotel_address">Address</Label>
                          <Input
                            id="new_hotel_address"
                            value={newHotel.address}
                            onChange={(e) => setNewHotel({ ...newHotel, address: e.target.value })}
                            placeholder="Hotel address"
                          />
                        </div>
                      </div>
                      {renderHotelItineraryPicker(
                        newHotelItineraryIds,
                        setNewHotelItineraryIds,
                        'new-hotel-itinerary',
                        newHotelStayDates,
                        setNewHotelStayDates
                      )}
                      <div className="rounded-md border p-2.5">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-wedding-charcoal">Hotel Manager Login (optional)</p>
                            <p className="text-xs text-muted-foreground">Add access only when needed.</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 shrink-0 px-2 text-xs"
                            onClick={() => setShowHotelManagerFields((current) => !current)}
                          >
                            {showHotelManagerFields ? 'Hide' : 'Add manager'}
                          </Button>
                        </div>
                        {showHotelManagerFields && (
                          <div className="mt-3 space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              <div>
                                <Label htmlFor="hotel-manager-name">Name</Label>
                                <Input
                                  id="hotel-manager-name"
                                  value={hotelManagerForm.name}
                                  onChange={(e) => setHotelManagerForm({ ...hotelManagerForm, name: e.target.value })}
                                />
                              </div>
                              <div>
                                <Label htmlFor="hotel-manager-phone">Phone</Label>
                                <Input
                                  id="hotel-manager-phone"
                                  value={hotelManagerForm.phone}
                                  onChange={(e) => setHotelManagerForm({ ...hotelManagerForm, phone: e.target.value })}
                                  placeholder="+91 98765 43210"
                                />
                              </div>
                            </div>
                            <div>
                              <Label htmlFor="hotel-manager-email">Email</Label>
                              <Input
                                id="hotel-manager-email"
                                type="email"
                                value={hotelManagerForm.email}
                                onChange={(e) => setHotelManagerForm({ ...hotelManagerForm, email: e.target.value })}
                              />
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                              <div>
                                <Label htmlFor="hotel-manager-password">Password</Label>
                                <Input
                                  id="hotel-manager-password"
                                  type="text"
                                  value={hotelManagerForm.password}
                                  onChange={(e) => setHotelManagerForm({ ...hotelManagerForm, password: e.target.value })}
                                />
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setHotelManagerForm({ ...hotelManagerForm, password: generatePassword() })}
                              >
                                Generate Password
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90">
                          Add Hotel
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
        </StickyActionHeader>

        <Dialog
          open={showAutoAllocationDialog}
          onOpenChange={(open) => {
            setShowAutoAllocationDialog(open);
            if (!open && !confirmingAutoAllocation) {
              setAutoAllocationPreview(null);
              setAutoAllocationHotelName('');
            }
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-hidden sm:max-w-[760px]">
            <DialogHeader>
              <DialogTitle>
                {autoAllocationHotelName
                  ? `Auto Allocation Preview: ${getDisplayText(autoAllocationHotelName, 'Hotel')}`
                  : 'Auto Allocation Preview'}
              </DialogTitle>
              <DialogDescription>
                Review this hotel&apos;s suggested room allocation before saving it.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1">
              {autoAllocationPreview ? (
                <>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                    {[
                      ['Bookings', autoAllocationPreview.summary?.allocated_bookings || 0],
                      ['People', autoAllocationPreview.summary?.allocated_people || 0],
                      ['Rooms', autoAllocationPreview.summary?.rooms_used || 0],
                      ['Split', autoAllocationPreview.summary?.split_bookings || 0],
                      ['Skipped', autoAllocationPreview.summary?.unallocated_bookings || 0],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-md border bg-muted/30 p-2">
                        <p className="text-[0.6875rem] font-medium uppercase text-muted-foreground">{label}</p>
                        <p className="text-lg font-semibold text-wedding-charcoal">{value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-wedding-charcoal">Suggested allocations</p>
                      <Badge variant="secondary">
                        {(autoAllocationPreview.proposals || []).length} booking(s)
                      </Badge>
                    </div>
                    {(autoAllocationPreview.proposals || []).length > 0 ? (
                      <div className="space-y-2">
                        {(autoAllocationPreview.proposals || []).map((proposal) => (
                          <div key={proposal.guest_id} className="rounded-md border p-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <p className="text-sm font-semibold text-wedding-charcoal">
                                  {getDisplayText(proposal.guest_name, 'Guest')}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {proposal.party_size || 1} people
                                  {proposal.side && proposal.side !== 'both' ? ` | ${proposal.side} side` : ' | both sides'}
                                </p>
                              </div>
                              {proposal.split && (
                                <Badge variant="outline" className="border-amber-300 text-amber-700">
                                  Split
                                </Badge>
                              )}
                            </div>
                            <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                              <p>
                                <span className="font-medium text-wedding-charcoal">Hotel:</span>{' '}
                                {getDisplayText(proposal.hotel_name, 'Hotel')} | {getDisplayText(proposal.stay_plan_title, 'Hotel event days')}
                              </p>
                              <p>
                                <span className="font-medium text-wedding-charcoal">Rooms:</span>{' '}
                                {(proposal.room_allocations || []).map((allocation) => (
                                  `Room ${getDisplayText(allocation.room_number, allocation.room_id)} (${allocation.beds || 0} bed${allocation.beds === 1 ? '' : 's'})`
                                )).join(' | ')}
                              </p>
                              {(proposal.events || []).length > 0 && (
                                <p>
                                  <span className="font-medium text-wedding-charcoal">Events:</span>{' '}
                                  {proposal.events.join(' | ')}
                                </p>
                              )}
                              {(proposal.reasons || []).length > 0 && (
                                <p>
                                  <span className="font-medium text-wedding-charcoal">Why:</span>{' '}
                                  {proposal.reasons.join(' | ')}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                        No eligible guest bookings could be auto allocated.
                      </div>
                    )}
                  </div>

                  {(autoAllocationPreview.unallocated || []).length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-wedding-charcoal">Skipped bookings</p>
                        <Badge variant="outline">
                          {(autoAllocationPreview.unallocated || []).length}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        {(autoAllocationPreview.unallocated || []).map((item) => (
                          <div key={item.guest_id} className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-xs">
                            <p className="font-semibold text-wedding-charcoal">
                              {getDisplayText(item.guest_name, 'Guest')} ({item.party_size || 1} people)
                            </p>
                            <p className="mt-1 text-muted-foreground">{getDisplayText(item.reason, 'Not eligible')}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  No preview loaded.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowAutoAllocationDialog(false)}
                disabled={confirmingAutoAllocation}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirmAutoAllocation}
                disabled={confirmingAutoAllocation || !(autoAllocationPreview?.proposals || []).length}
                className="bg-wedding-sage hover:bg-wedding-sage/90"
              >
                {confirmingAutoAllocation ? 'Allocating...' : 'Confirm Allocation'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showAddRoomDialog} onOpenChange={setShowAddRoomDialog}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Room</DialogTitle>
              <DialogDescription>
                Add a room to {getDisplayText(selectedHotelForRoom, 'this hotel')}.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddRoom} className="space-y-3">
              <div>
                <Label htmlFor="room_hotel_name">Hotel</Label>
                <Input
                  id="room_hotel_name"
                  value={selectedHotelForRoom}
                  readOnly
                  className="bg-muted cursor-not-allowed"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="room_number">Room Number *</Label>
                  <Input
                    id="room_number"
                    value={newRoom.room_number}
                    onChange={(e) => setNewRoom({ ...newRoom, room_number: e.target.value })}
                    required
                    data-testid="room-number-input"
                  />
                </div>
                <div>
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input
                    id="capacity"
                    type="number"
                    min="1"
                    value={newRoom.capacity}
                    onChange={(e) => setNewRoom({ ...newRoom, capacity: parseInt(e.target.value, 10) || 1 })}
                    data-testid="room-capacity-input"
                  />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="floor">Floor</Label>
                  <Input
                    id="floor"
                    value={newRoom.floor}
                    onChange={(e) => setNewRoom({ ...newRoom, floor: e.target.value })}
                    data-testid="room-floor-input"
                  />
                </div>
                <div>
                  <Label htmlFor="wing">Wing/Block</Label>
                  <Input
                    id="wing"
                    value={newRoom.wing}
                    onChange={(e) => setNewRoom({ ...newRoom, wing: e.target.value })}
                    data-testid="room-wing-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90" data-testid="save-room-btn">
                  Add Room
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showEditRoomDialog}
          onOpenChange={(open) => {
            setShowEditRoomDialog(open);
            if (!open) setEditingRoom(null);
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Room</DialogTitle>
              <DialogDescription>
                Update room details for {getDisplayText(editingRoom?.hotel_name, 'this hotel')}.
              </DialogDescription>
            </DialogHeader>
            {editingRoom && (
              <form onSubmit={handleEditRoom} className="space-y-3">
                <div>
                  <Label htmlFor="edit_room_hotel_name">Hotel</Label>
                  <Input
                    id="edit_room_hotel_name"
                    value={editingRoom.hotel_name}
                    readOnly
                    className="bg-muted cursor-not-allowed"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="edit_room_number">Room Number *</Label>
                    <Input
                      id="edit_room_number"
                      value={editingRoom.room_number}
                      onChange={(e) => setEditingRoom({ ...editingRoom, room_number: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_room_capacity">Capacity</Label>
                    <Input
                      id="edit_room_capacity"
                      type="number"
                      min="1"
                      value={editingRoom.capacity}
                      onChange={(e) => setEditingRoom({ ...editingRoom, capacity: parseInt(e.target.value, 10) || 1 })}
                    />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="edit_room_floor">Floor</Label>
                    <Input
                      id="edit_room_floor"
                      value={editingRoom.floor}
                      onChange={(e) => setEditingRoom({ ...editingRoom, floor: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_room_wing">Wing/Block</Label>
                    <Input
                      id="edit_room_wing"
                      value={editingRoom.wing}
                      onChange={(e) => setEditingRoom({ ...editingRoom, wing: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={updatingRoom}
                    className="bg-wedding-sage hover:bg-wedding-sage/90"
                  >
                    {updatingRoom ? 'Saving...' : 'Save Room'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <WhatsAppSendConfirmationDialog
          open={Boolean(pendingRoomMessage)}
          onOpenChange={(open) => !open && setPendingRoomMessage(null)}
          onConfirm={() => {
            const targetRoom = pendingRoomMessage?.room || null;
            const recipientCount = pendingRoomMessage?.guestCount || 0;
            setPendingRoomMessage(null);
            toast.info(
              recipientCount === 1
                ? 'Sending WhatsApp message to 1 recipient...'
                : `Sending WhatsApp message to ${recipientCount} recipients...`,
            );
            sendAccommodationUpdate(targetRoom);
          }}
          title={pendingRoomMessage?.room
            ? `Send update for Room ${getDisplayText(pendingRoomMessage.room.room_number)}?`
            : 'Send room updates to all allocated guests?'}
          description={pendingRoomMessage?.room
            ? `This will send the latest accommodation details on WhatsApp to ${pendingRoomMessage.guestCount || 0} guest(s) allocated to this room.`
            : 'This will send the latest accommodation details on WhatsApp to all guests with room allocations.'}
          confirmLabel="Send room update"
          templateName="accomodation_update_notifications"
          templateVariables={{
            guestName: 'Guest',
            hostNames: [currentEvent?.bride_name, currentEvent?.groom_name].filter(Boolean).join(' & ')
              || currentEvent?.name
              || 'the couple',
            managerName: user?.company_name || user?.name || 'Your Event Team',
            hotelName: pendingRoomMessage?.room?.hotel_name || selectedStayPlan?.hotel_name || 'Hotel',
            roomNumber: pendingRoomMessage?.room?.room_number || 'Assigned room',
            checkInDate: selectedStayPlan?.check_in_date || 'Check-in date',
            checkOutDate: selectedStayPlan?.check_out_date || 'Check-out date',
          }}
          disabled={Boolean(sendingRoomMessageScope)}
          testId="confirm-room-whatsapp-send-btn"
        />

        <AlertDialog open={Boolean(roomToDelete)} onOpenChange={(open) => !open && setRoomToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Room {getDisplayText(roomToDelete?.room_number)}?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the room from allocation. Rooms with assigned guests must be emptied before deletion.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingRoom}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  handleDeleteRoom();
                }}
                disabled={deletingRoom}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingRoom ? 'Deleting...' : 'Delete Room'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={Boolean(hotelToDelete)} onOpenChange={(open) => !open && setHotelToDelete('')}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {getDisplayText(hotelToDelete, 'this hotel')}?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the hotel setup, its rooms, and its tagged event days. Hotels with assigned guests or tagged event-day assignments must be emptied before deletion.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingHotel}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  handleDeleteHotel();
                }}
                disabled={deletingHotel}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingHotel ? 'Deleting...' : 'Delete Hotel'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={Boolean(stayPlanToDelete)} onOpenChange={(open) => !open && setStayPlanToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {getStayPlanTitle(stayPlanToDelete)}?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the tagged event-day setup and any room assignments saved inside it. The hotel and rooms will stay.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingStayPlan}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  handleDeleteStayPlan();
                }}
                disabled={deletingStayPlan}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingStayPlan ? 'Deleting...' : 'Delete Tagged Days'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog
          open={showEditHotelDialog}
          onOpenChange={(open) => {
            setShowEditHotelDialog(open);
            if (!open) {
              setHotelToEdit('');
              setEditedHotelName('');
              setEditHotelItineraryIds([]);
              setEditHotelStayDates({ check_in_date: '', check_out_date: '' });
              setEditingHotelManagerForm({ name: '', email: '', phone: '', password: '' });
              setShowEditHotelManagerFields(false);
            }
          }}
        >
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Hotel Details</DialogTitle>
              <DialogDescription>Update hotel details and the itinerary events this hotel covers.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditHotel} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="current_hotel">Current Hotel</Label>
                  <Input
                    id="current_hotel"
                    value={hotelToEdit}
                    readOnly
                    required
                    className="bg-muted cursor-not-allowed"
                  />
                </div>
                <div>
                  <Label htmlFor="new_hotel_name">New Hotel Name</Label>
                  <Input
                    id="new_hotel_name"
                    value={editedHotelName}
                    onChange={(e) => setEditedHotelName(e.target.value)}
                    required
                  />
                </div>
              </div>
              {renderHotelItineraryPicker(
                editHotelItineraryIds,
                setEditHotelItineraryIds,
                'edit-hotel-itinerary',
                editHotelStayDates,
                setEditHotelStayDates,
                false
              )}
              {isEventManager && (
                <div className="rounded-md border p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-wedding-charcoal">Hotel Manager</p>
                      <p className="text-xs text-muted-foreground">
                        {currentHotelManager ? `${currentHotelManager.name || 'Manager'}${currentHotelManager.phone ? ` | ${currentHotelManager.phone}` : ''}` : 'Optional. Add phone and password login.'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 px-2 text-xs"
                      onClick={() => setShowEditHotelManagerFields((current) => !current)}
                    >
                      {showEditHotelManagerFields ? 'Hide' : currentHotelManager ? 'Edit manager' : 'Add manager'}
                    </Button>
                  </div>
                  {showEditHotelManagerFields && (
                    <div className="mt-3 space-y-3">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="edit-hotel-manager-name">Name</Label>
                          <Input
                            id="edit-hotel-manager-name"
                            value={editingHotelManagerForm.name}
                            onChange={(e) => setEditingHotelManagerForm({ ...editingHotelManagerForm, name: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-hotel-manager-phone">Phone</Label>
                          <Input
                            id="edit-hotel-manager-phone"
                            value={editingHotelManagerForm.phone}
                            onChange={(e) => setEditingHotelManagerForm({ ...editingHotelManagerForm, phone: e.target.value })}
                            placeholder="+91 98765 43210"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="edit-hotel-manager-email">Email</Label>
                        <Input
                          id="edit-hotel-manager-email"
                          type="email"
                          value={editingHotelManagerForm.email}
                          onChange={(e) => setEditingHotelManagerForm({ ...editingHotelManagerForm, email: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                        <div>
                          <Label htmlFor="edit-hotel-manager-password">Password</Label>
                          <Input
                            id="edit-hotel-manager-password"
                            type="text"
                            value={editingHotelManagerForm.password}
                            placeholder={currentHotelManager ? 'Leave blank to keep current password' : ''}
                            onChange={(e) => setEditingHotelManagerForm({ ...editingHotelManagerForm, password: e.target.value })}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setEditingHotelManagerForm({ ...editingHotelManagerForm, password: generatePassword() })}
                        >
                          {currentHotelManager ? 'Reset Password' : 'Generate Password'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90">
                  Save Hotel Details
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
          <Card>
            <CardContent className="flex items-center gap-2 p-3 sm:gap-3 sm:p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-wedding-sage/10 sm:h-10 sm:w-10">
                <Bed className="h-4 w-4 text-wedding-sage sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold leading-none text-wedding-charcoal sm:text-2xl">{rooms.length}</p>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">Total Rooms</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-2 p-3 sm:gap-3 sm:p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-100 sm:h-10 sm:w-10">
                <Users className="h-4 w-4 text-blue-600 sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold leading-none text-wedding-charcoal sm:text-2xl">{guestsNeedingRooms}</p>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">Need Rooms</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-2 p-3 sm:gap-3 sm:p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-wedding-gold/10 sm:h-10 sm:w-10">
                <Users className="h-4 w-4 text-wedding-gold sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold leading-none text-wedding-charcoal sm:text-2xl">{totalOccupancy}</p>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">Assigned</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-2 p-3 sm:gap-3 sm:p-4">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-wedding-rose/30 sm:h-10 sm:w-10">
                <Building className="h-4 w-4 text-wedding-charcoal sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold leading-none text-wedding-charcoal sm:text-2xl">{Object.keys(hotelGroups).length}</p>
                <p className="mt-1 text-xs text-muted-foreground sm:text-sm">Hotels</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList>
            <TabsTrigger value="rooms">Hotels & Rooms</TabsTrigger>
            <TabsTrigger value="arrivals">Arrivals</TabsTrigger>
          </TabsList>

          <TabsContent value="arrivals" className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap gap-2">
                    {arrivalFilters.map((filter) => (
                      <Button
                        key={filter.value}
                        type="button"
                        size="sm"
                        variant={arrivalFilter === filter.value ? 'default' : 'outline'}
                        onClick={() => handleArrivalFilterChange(filter.value)}
                        className={arrivalFilter === filter.value ? 'bg-wedding-sage hover:bg-wedding-sage/90' : ''}
                      >
                        {filter.label}
                      </Button>
                    ))}
                  </div>
                  <div className="relative w-full lg:max-w-sm">
                    <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={arrivalSearchQuery}
                      onChange={(event) => setArrivalSearchQuery(event.target.value)}
                      placeholder="Search guest, phone, hotel, or room..."
                      className="pl-9"
                      data-testid="arrival-search-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              {filteredArrivals.map((item) => {
                const guest = item.guest;
                const room = item.room;
                return (
                  <Card
                    key={guest.id}
                    className="cursor-pointer transition-shadow hover:shadow-md"
                    onClick={() => setSelectedArrival(item)}
                    data-testid={`arrival-${guest.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-wedding-charcoal">{getDisplayText(guest.name, 'Guest')}</h3>
                            <Badge className={journeyTone[item.journey_status] || 'bg-slate-100 text-slate-700'}>
                              {getDisplayText(item.journey_label)}
                            </Badge>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-3 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" /> {formatEta(item.eta_to_hotel)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Bed className="h-4 w-4" />
                              {room ? `${getDisplayText(room.hotel_name)} / Room ${getDisplayText(room.room_number)}` : 'No room assigned'}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-md bg-wedding-sage/10 px-3 py-2 text-sm font-medium text-wedding-sage">
                          Next: {getDisplayText(item.next_action)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {filteredArrivals.length === 0 && !arrivalsLoading && (
                <Card>
                  <CardContent className="py-10 text-center text-muted-foreground">
                    {arrivalSearchQuery
                      ? `No guests match "${arrivalSearchQuery}".`
                      : 'No guests match this arrivals filter.'}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="rooms" className="space-y-6">
        {deferredStayEvents.length > 0 && (
          <div className="flex flex-col gap-3 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-amber-950 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <WarningCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-medium">
                  {deferredStayEvents.length} {deferredStayEvents.length === 1 ? 'event needs' : 'events need'} a hotel
                </p>
                <p className="text-sm">
                  {deferredStayEvents.map((item) => getDisplayText(item.name, 'Event')).join(', ')}
                </p>
              </div>
            </div>
            {isEventManager && (
              <Button asChild variant="outline" size="sm" className="border-amber-300 bg-white">
                <Link to="/dashboard/itinerary">Configure stays</Link>
              </Button>
            )}
          </div>
        )}
        {/* Rooms by Hotel */}
        {Object.entries(hotelGroups).map(([hotel, hotelRooms]) => {
          const sectionKey = getHotelSectionKey(hotel);
          const isCollapsed = isHotelSectionCollapsed(hotel);
          const hotelSearchQuery = hotelRoomSearches[sectionKey] || '';
          const visibleHotelRooms = getFilteredHotelRooms(hotelRooms, hotelSearchQuery);
          const hotelGuestCount = getHotelGuestCount(hotelRooms);
          const hotelAvailableBeds = hotelRooms.reduce(
            (total, room) => total + getRoomAvailableBeds(room),
            0,
          );
          const hotelStayPlans = getStayPlansForHotel(hotel);
          const hotelProfile = getHotelProfile(hotel);
          const invalidStayPlans = hotelStayPlans.filter((plan) => getStayPlanStatus(plan) === 'invalid').length;
          const hotelTaggedItineraryIds = Array.from(new Set(
            hotelStayPlans.flatMap((plan) => plan.itinerary_event_ids || [])
          ));
          const hotelTaggedEvents = hotelTaggedItineraryIds.map((itineraryId) => ({
            itineraryId,
            item: getItineraryById(itineraryId),
            plan: hotelStayPlans.find((plan) => (plan.itinerary_event_ids || []).includes(itineraryId))
          }));
          const activeHotelStayPlan = selectedStayPlan && normalizeValue(selectedStayPlan.hotel_name) === normalizeValue(hotel)
            ? selectedStayPlan
            : null;
          const defaultAllocationPlan = activeHotelStayPlan || hotelStayPlans.find((plan) => canAllocateStayPlan(plan)) || hotelStayPlans[0];
          const aggregateStayDates = getAggregateStayDatesForHotel(hotel);
          const hotelStayDateLabel = aggregateStayDates.check_in_date && aggregateStayDates.check_out_date
            ? `${formatDateOnly(aggregateStayDates.check_in_date)} to ${formatDateOnly(aggregateStayDates.check_out_date)}`
            : 'No hotel dates set';
          const hotelAllocationRequestKey = getHotelSectionKey(hotel);

          return (
          <Card key={hotel} className="overflow-hidden rounded-lg">
            <CardHeader
              className="cursor-pointer p-3 transition-colors hover:bg-muted/30"
              role="button"
              tabIndex={0}
              aria-expanded={!isCollapsed}
              onClick={() => toggleHotelSection(hotel)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleHotelSection(hotel);
                }
              }}
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <CardTitle className="flex flex-wrap items-center gap-1.5 text-base sm:gap-2 sm:text-lg">
                    {isCollapsed ? (
                      <CaretRight className="h-4 w-4 shrink-0 text-muted-foreground" weight="bold" />
                    ) : (
                      <CaretDown className="h-4 w-4 shrink-0 text-muted-foreground" weight="bold" />
                    )}
                    <Building className="h-4 w-4 shrink-0 text-wedding-sage sm:h-5 sm:w-5" />
                    <span className="max-w-[170px] truncate sm:max-w-none">{getDisplayText(hotel, 'Hotel')}</span>
                    <Badge variant="outline" className="whitespace-nowrap font-normal">
                      {hotelAvailableBeds} {hotelAvailableBeds === 1 ? 'bed' : 'beds'} available
                    </Badge>
                  </CardTitle>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline"><Bed className="mr-1 h-3 w-3" />{hotelRooms.length}</Badge>
                    <Badge variant="outline"><Users className="mr-1 h-3 w-3" />{hotelGuestCount}</Badge>
                    <Badge variant="outline"><Clock className="mr-1 h-3 w-3" />{hotelStayDateLabel}</Badge>
                    {invalidStayPlans > 0 && (
                      <Badge className="bg-red-100 text-red-700">
                        {invalidStayPlans} invalid
                      </Badge>
                    )}
                  </div>
                </div>
                <div
                  className="flex w-full items-center justify-end gap-2 sm:w-auto"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {isEventManager && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" aria-label="Hotel actions">
                          <DotsThree style={{ width: 24, height: 24 }} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => {
                          const manager = hotelManagerByHotel[normalizeValue(hotel)];
                          setHotelToEdit(hotel);
                          setEditedHotelName(hotel);
                          setEditingHotelManagerForm({
                            name: manager?.name || '',
                            email: manager?.email || '',
                            phone: manager?.phone || '',
                            password: ''
                          });
                          setShowEditHotelManagerFields(Boolean(manager));
                          setEditHotelItineraryIds(hotelTaggedItineraryIds);
                          setEditHotelStayDates(getStayDatesForSelectedEvents(hotelTaggedItineraryIds, defaultAllocationPlan));
                          setShowEditHotelDialog(true);
                        }}>
                          <PencilSimple className="h-4 w-4" /> Edit hotel
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setHotelToDelete(hotel)} className="text-red-600 focus:text-red-700">
                          <Trash className="h-4 w-4" /> Delete hotel
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {(isEventManager || isHotelManager) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
                      onClick={() => handlePreviewAutoAllocation(hotel)}
                      disabled={
                        Boolean(previewingAutoAllocation) ||
                        loading ||
                        hotelRooms.length === 0 ||
                        guests.length === 0 ||
                        hotelStayPlans.length === 0
                      }
                      data-testid={`auto-allocate-rooms-${hotelAllocationRequestKey.replace(/\s+/g, '-')}`}
                    >
                      <Bed className="mr-1 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      {previewingAutoAllocation === hotelAllocationRequestKey ? 'Planning...' : 'Auto Allocate'}
                    </Button>
                  )}
                  {(isEventManager || isHotelManager) && (
                    <Button
                      size="sm"
                      className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
                      onClick={() => {
                        setSelectedHotelForRoom(hotel);
                        setNewRoom({
                          room_number: '',
                          floor: '',
                          wing: '',
                          capacity: 2
                        });
                        setShowAddRoomDialog(true);
                      }}
                      data-testid={`add-room-to-${normalizeValue(hotel).replace(/\s+/g, '-')}`}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5 sm:h-4 sm:w-4" /> Add Room
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            {!isCollapsed && (
            <CardContent className="space-y-2 px-3 pb-3 pt-0">
              {(hotelProfile?.venue_name || hotelProfile?.address || hotelProfile?.venue_address || hotelTaggedEvents.length > 0) && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  {hotelProfile?.venue_name && <span className="font-medium text-foreground">{hotelProfile.venue_name}</span>}
                  {(hotelProfile?.address || hotelProfile?.venue_address) && (
                    <span className="inline-flex min-w-0 items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{hotelProfile.address || hotelProfile.venue_address}</span>
                    </span>
                  )}
                  {hotelTaggedEvents.length > 0 && (
                    <span>{hotelTaggedEvents.map(({ item }) => getDisplayText(item?.name, 'Event day')).join(' • ')}</span>
                  )}
                </div>
              )}
              {hotelRooms.length > 0 && (
                <div className="relative flex items-center gap-2">
                  <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={hotelSearchQuery}
                    onChange={(event) => updateHotelRoomSearch(hotel, event.target.value)}
                    placeholder="Search rooms or guests..."
                    className="h-9 truncate bg-white pl-9 text-sm"
                    data-testid={`hotel-room-search-${sectionKey.replace(/\s+/g, '-')}`}
                  />
                  <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
                    {visibleHotelRooms.length} visible
                  </span>
                </div>
              )}
              {hotelRooms.length === 0 ? (
                <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">
                  No rooms added for this hotel yet.
                </div>
              ) : visibleHotelRooms.length === 0 ? (
                <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">
                  {`No rooms found matching "${hotelSearchQuery}".`}
                </div>
              ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
                {visibleHotelRooms.map((room) => {
                  const occupancy = getRoomAssignedOccupants(room);
                  const assignmentRows = getRoomAssignmentRows(room);
                  const roomAllocationClashes = getRoomAllocationClashes(room);
                  const hasRoomAllocationClash = roomAllocationClashes.length > 0;
                  const roomCapacity = Number(room.capacity) || 0;
                  const safeRoomCapacity = Math.max(roomCapacity, 1);
                  const isFull = occupancy >= roomCapacity;
                  const isOverCapacity = occupancy > roomCapacity;
                  const occupancyPercent = Math.min(100, Math.round((occupancy / safeRoomCapacity) * 100));
                  
                  return (
                    <Card 
                      key={room.id} 
                      className={`rounded-xl border bg-white shadow-sm transition-shadow hover:shadow-md ${hasRoomAllocationClash ? 'border-red-300 bg-red-50/60' : isFull ? 'border-wedding-gold/50 bg-wedding-gold/5' : 'border-slate-200'}`}
                      data-testid={`room-card-${room.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="mb-2">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className="min-w-0 break-words text-base font-semibold leading-snug"
                              title={`Room ${getDisplayText(room.room_number)}`}
                            >
                              Room {getDisplayText(room.room_number)}
                            </p>
                            <div className="flex shrink-0 items-center gap-1">
                              <div className="sm:hidden">
                                {renderRoomStatusDropdown(room)}
                              </div>
                              {(isEventManager || isHotelManager) && (
                                <>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => openEditRoomDialog(room)}
                                    aria-label={`Edit Room ${getDisplayText(room.room_number)}`}
                                    title="Edit room"
                                    data-testid={`edit-room-${room.id}`}
                                  >
                                    <PencilSimple className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-red-600 hover:bg-red-50 hover:text-red-700"
                                    onClick={() => setRoomToDelete(room)}
                                    aria-label={`Delete Room ${getDisplayText(room.room_number)}`}
                                    title={occupancy > 0 ? 'Remove assigned guests before deleting this room' : 'Delete room'}
                                    data-testid={`delete-room-${room.id}`}
                                  >
                                    <Trash className="h-3.5 w-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="mt-0.5 flex min-h-7 items-center justify-between gap-2">
                            <p className="min-w-0 break-words text-xs text-muted-foreground">
                              {room.floor && `Floor ${getDisplayText(room.floor)}`}
                              {room.wing && ` • ${getDisplayText(room.wing)}`}
                            </p>
                            <div className="hidden shrink-0 sm:block">
                              {renderRoomStatusDropdown(room)}
                            </div>
                          </div>
                        </div>

                        {hasRoomAllocationClash && (
                          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                            <p className="font-semibold">Allocation clash</p>
                            {roomAllocationClashes.map((message) => (
                              <p key={getDisplayText(message)} className="mt-1">{getDisplayText(message)}</p>
                            ))}
                          </div>
                        )}

                        <div className="mb-2 rounded-lg bg-slate-50 px-2 py-1.5">
                          <div className="flex items-center gap-2 text-xs">
                            <Bed className="h-3.5 w-3.5 text-wedding-sage" />
                            <span className="font-medium text-wedding-charcoal">
                              {occupancy}/{roomCapacity} beds used
                            </span>
                            <span className={`ml-auto font-medium ${isOverCapacity ? 'text-red-600' : 'text-muted-foreground'}`}>
                              {isOverCapacity ? `${occupancy - roomCapacity} over` : `${Math.max(roomCapacity - occupancy, 0)} free`}
                            </span>
                          </div>
                          <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-slate-200">
                            <div
                              className={`h-full rounded-full ${isOverCapacity ? 'bg-red-500' : isFull ? 'bg-wedding-gold' : 'bg-wedding-sage'}`}
                              style={{ width: `${occupancyPercent}%` }}
                            />
                          </div>
                        </div>

                        {/* Assigned Guests */}
                        <div className="mb-2 space-y-1.5">
                          {assignmentRows.map((assignmentRow) => {
                            const guestId = assignmentRow.guestId;
                            const guest = getGuestById(guestId);
                            const occupantCount = assignmentRow.allocatedBeds || getRoomGuestAllocatedBeds(room, guestId);
                            const bookingPeopleCount = getGuestOccupantCount(guest);
                            const { namedOccupants, unnamedOccupantCount } = getRoomNamedOccupantsForAssignment(guest, occupantCount);
                            const displayGuest = namedOccupants[0] || guest;
                            const companionMembers = namedOccupants.filter((member) => member.id !== displayGuest?.id);
                            const canUnassignRow = isEventManager || isHotelManager;
                            const sourceLabelText = assignmentRow.sourceLabels.map(getDisplayText).filter(Boolean).join(' + ');
                            return guest ? (
                              <div key={guestId} className="flex items-center justify-between gap-2 rounded-md bg-wedding-sage/10 px-2 py-1">
                                <div className="flex min-w-0 items-center gap-1.5">
                                  <User className="h-3.5 w-3.5 shrink-0 text-wedding-sage" />
                                  <div className="min-w-0">
                                    <div className="flex min-w-0 items-center gap-1.5">
                                      <span className="truncate text-xs font-medium">{getDisplayText(displayGuest?.name, 'Guest')}</span>
                                      {bookingPeopleCount > 1 && (
                                        <span className="shrink-0 text-[0.625rem] text-muted-foreground">
                                          {occupantCount}/{bookingPeopleCount} people
                                        </span>
                                      )}
                                    </div>
                                    {(companionMembers.length > 0 || unnamedOccupantCount > 0) && (
                                      <p className="truncate text-xs text-muted-foreground">
                                        {[
                                          ...companionMembers.map((member) => getDisplayText(member.name, 'Guest')),
                                          unnamedOccupantCount > 0
                                            ? `${unnamedOccupantCount} unnamed ${unnamedOccupantCount === 1 ? 'guest' : 'guests'}`
                                            : ''
                                        ].filter(Boolean).join(', ')}
                                      </p>
                                    )}
                                    {sourceLabelText && (
                                      <p className="truncate text-[0.625rem] text-emerald-700">
                                        {sourceLabelText}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {canUnassignRow && (
                                  <button
                                    type="button"
                                    onClick={() => handleUnassignGuest(room.id, guestId, {
                                      stayPlanIds: assignmentRow.sourcePlanIds,
                                      removeLegacy: assignmentRow.hasLegacyAssignment
                                    })}
                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-red-50 hover:text-red-600"
                                    data-testid={`unassign-${guestId}`}
                                    title="Remove from room"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            ) : null;
                          })}
                        </div>

                        {/* Add Guest Buttons */}
                        {!isFull && (
                          <div className="grid grid-cols-1 gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-full px-2 text-xs"
                              onClick={() => openRoomAssignmentDialog(room)}
                              title="Add guest to this room"
                              data-testid={`assign-to-${room.id}`}
                            >
                              <Plus className="mr-1 h-3.5 w-3.5" /> Add Guest
                            </Button>
                          </div>
                        )}
                        {occupancy > 0 && (
                          <div className="mt-1.5 grid grid-cols-1 gap-1.5">
                            {(isEventManager || isHotelManager) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-full px-2 text-xs"
                                onClick={() => requestAccommodationUpdate(room)}
                                disabled={sendingRoomMessageScope === room.id}
                                data-testid={`send-room-message-${room.id}`}
                              >
                                <WhatsappLogo className="mr-1 h-3.5 w-3.5" />
                                {sendingRoomMessageScope === room.id ? 'Sending...' : 'Send Update'}
                              </Button>
                            )}
                          </div>
                        )}
                        {isOverCapacity && !hasRoomAllocationClash && (
                          <p className="text-xs text-red-600 mt-2 text-center">
                            Over capacity! Please reassign guests.
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              )}
            </CardContent>
            )}
          </Card>
          );
        })}

        {Object.keys(hotelGroups).length === 0 && !loading && (
          <Card>
            <CardContent className="py-12 text-center">
              <Bed className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No hotels added yet. Add your first hotel to start allocating rooms.</p>
            </CardContent>
          </Card>
        )}
          </TabsContent>
        </Tabs>

        <Sheet open={Boolean(selectedArrival)} onOpenChange={(open) => !open && setSelectedArrival(null)}>
          <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
            <SheetHeader>
              <SheetTitle>{selectedGuest?.name || 'Guest Journey'}</SheetTitle>
              <SheetDescription>
                Current stage and next recommended hotel operation.
              </SheetDescription>
            </SheetHeader>
            {selectedArrival && (
              <div className="mt-6 space-y-5">
                <div className="rounded-lg border p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Current Status</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge className={journeyTone[selectedArrival.journey_status] || 'bg-slate-100 text-slate-700'}>
                      {getDisplayText(selectedArrival.journey_label)}
                    </Badge>
                  </div>
                  <p className="mt-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">Next Action</p>
                  <p className="mt-1 font-semibold text-wedding-charcoal">{getDisplayText(selectedArrival.next_action)}</p>
                </div>

                <div className="grid gap-3 text-sm">
                  <div className="flex items-center justify-between gap-3 border-b pb-2">
                    <span className="text-muted-foreground">ETA</span>
                    <span className="font-medium">{formatEta(selectedArrival.eta_to_hotel)}</span>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-b pb-2">
                    <span className="text-muted-foreground">Assigned Room</span>
                    <span className="text-right font-medium">
                      {selectedRoomForArrival
                        ? `${getDisplayText(selectedRoomForArrival.hotel_name)} / Room ${getDisplayText(selectedRoomForArrival.room_number)}`
                        : 'Not assigned'}
                    </span>
                  </div>
                  {selectedGuest?.journey_notes && (
                    <div className="rounded-md bg-muted p-3">
                      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Notes</p>
                      <p className="mt-1">{selectedGuest.journey_notes}</p>
                    </div>
                  )}
                </div>

                <div>
                  <p className="mb-2 text-sm font-medium text-wedding-charcoal">Quick Actions</p>
                  {renderJourneyActions()}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Assign Guest Dialog */}
        <Dialog
          open={showAssignDialog}
          onOpenChange={(open) => {
            setShowAssignDialog(open);
            if (!open) {
              setSelectedRoom(null);
              setSelectedGuestIds([]);
              setGuestSearchQuery('');
              setAssignmentRoomNeedFilter('needs_room');
              setAssignmentSideFilter('both');
              setAssignmentItineraryFilterIds([]);
              setAllowSplitOverride(false);
              setAssignmentStayPlanId('');
              setAssignmentMode('invited');
              setIsAssignmentEligibilityOpen(false);
              setAdHocGuest({ name: '', phone: '', email: '' });
              setShowAdHocGuestForm(false);
            }
          }}
        >
          <DialogContent className="flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] flex-col overflow-hidden border-[#E4D8CA] bg-[#FBFAF8] p-0 shadow-[0_24px_70px_rgba(47,42,38,0.22)] sm:max-h-[90vh] sm:max-w-[640px]">
            <DialogHeader className="border-b border-[#E8DED3] bg-white px-5 py-4 pr-12">
              <DialogTitle className="text-xl leading-tight">Assign Guests</DialogTitle>
              <DialogDescription className="text-sm">
                Room {getDisplayText(selectedRoom?.room_number)} | {getDisplayText(selectedRoom?.hotel_name)}
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
              <div className="grid gap-2 rounded-md border border-[#E7D8C8] bg-[#FCF8F3] p-2.5 text-sm sm:grid-cols-[1fr_1fr_auto] sm:items-center">
                <div className="flex items-center gap-2 text-wedding-charcoal">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-[#8B654E] shadow-sm">
                    <Bed className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Capacity</p>
                    <p className="font-semibold">{selectedRoom?.capacity || 0} beds</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-wedding-charcoal">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white text-[#8B654E] shadow-sm">
                    <Users className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Selected</p>
                    <p className="font-semibold">{selectedPeopleRequired} {selectedPeopleRequired === 1 ? 'person' : 'people'}</p>
                  </div>
                </div>
                <Badge className={`justify-center border px-2 py-1 text-xs ${
                  allocationExceeded
                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                    : 'border-blue-100 bg-blue-50 text-blue-700'
                }`}>
                  {allocationExceeded ? `${selectedOverCapacityBy} over` : `${remainingBedsAfterSelection} free`}
                </Badge>
              </div>  

              <div className="rounded-md border border-[#E8DED3] bg-white p-3 shadow-sm" data-testid="room-allocation-filters">
                  <div className={cn('flex items-center justify-between gap-2', isAssignmentEligibilityOpen && 'mb-2')}>
                    <button
                      type="button"
                      onClick={() => setIsAssignmentEligibilityOpen((current) => !current)}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-wedding-charcoal"
                      aria-expanded={isAssignmentEligibilityOpen}
                    >
                      <Funnel className="h-4 w-4 text-[#8B654E]" />
                      Eligibility
                      <CaretDown className={cn('h-3.5 w-3.5 transition-transform', isAssignmentEligibilityOpen && 'rotate-180')} />
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {filteredAssignmentGuests.length} of {assignableGuestBookings.length} shown
                    </span>
                  </div>
                  {isAssignmentEligibilityOpen && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Room tag</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-1 h-9 w-full justify-between border-[#D8CBB9] bg-[#FBFAF8] px-3 text-sm"
                            data-testid={`allocation-room-need-filter-${assignmentRoomNeedFilter}`}
                          >
                            <span className="truncate">{assignmentRoomNeedLabel}</span>
                            <CaretDown className="h-3.5 w-3.5 shrink-0" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                          {[
                            {
                              value: 'needs_room',
                              label: 'Need room: Tagged',
                              helper: 'Default. Only guests marked as needing rooms.'
                            },
                            {
                              value: 'all',
                              label: 'All matching guests',
                              helper: 'Includes untagged guests while keeping other filters.'
                            },
                            {
                              value: 'show_all',
                              label: 'Show all guests',
                              helper: 'Bypasses room, status, side and itinerary filters.'
                            }
                          ].map((option) => (
                            <DropdownMenuItem
                              key={option.value}
                              onSelect={() => {
                                if (option.value === 'show_all') {
                                  handleShowAllAssignmentGuests();
                                } else {
                                  setAssignmentRoomNeedFilter(option.value);
                                }
                                setSelectedGuestIds((currentIds) => (
                                  option.value === 'needs_room'
                                    ? currentIds.filter((guestId) => guestRequiresRoom(getGuestById(guestId)))
                                    : currentIds
                                ));
                              }}
                            >
                              <span className="min-w-0">
                                <span className="block truncate">{option.label}</span>
                                <span className="block text-xs text-muted-foreground">{option.helper}</span>
                              </span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Room status</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-1 h-9 w-full justify-between border-[#D8CBB9] bg-[#FBFAF8] px-3 text-sm"
                            data-testid={`allocation-status-filter-${assignmentStatusFilter}`}
                          >
                            <span className="truncate">{assignmentStatusLabel}</span>
                            <CaretDown className="h-3.5 w-3.5 shrink-0" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-48">
                          {[
                            { value: 'unassigned', label: 'Room unassigned' },
                            { value: 'assigned', label: 'Room assigned' },
                            { value: 'both', label: 'Both' }
                          ].map((option) => (
                            <DropdownMenuItem
                              key={option.value}
                              onSelect={() => {
                                setAssignmentRoomNeedFilter((current) => current === 'show_all' ? 'all' : current);
                                setAssignmentStatusFilter(option.value);
                              }}
                            >
                              {option.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Itinerary events</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="mt-1 h-9 w-full justify-between border-[#D8CBB9] bg-[#FBFAF8] px-3 text-sm"
                            data-testid="allocation-itinerary-trigger"
                          >
                            <span className="min-w-0 truncate text-left">{assignmentEventSummary}</span>
                            <CaretDown className="h-3.5 w-3.5 shrink-0" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-72">
                          <DropdownMenuLabel className="text-xs text-muted-foreground">
                            Default selection is tagged to this hotel
                          </DropdownMenuLabel>
                          {assignmentItineraryOptions.length > 0 && (
                            <>
                              <DropdownMenuCheckboxItem
                                checked={assignmentItineraryFilterIds.length === assignmentItineraryOptions.length}
                                onCheckedChange={(checked) => setAssignmentItineraryFilterIds(
                                  checked === true ? assignmentItineraryOptions.map((item) => item.id) : []
                                )}
                                onSelect={(event) => {
                                  event.preventDefault();
                                  setAssignmentRoomNeedFilter((current) => current === 'show_all' ? 'all' : current);
                                }}
                              >
                                All itinerary events
                              </DropdownMenuCheckboxItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          {assignmentItineraryOptions.map((item) => {
                            const isTaggedToHotel = hotelTaggedItineraryIdSet.has(item.id);
                            return (
                              <DropdownMenuCheckboxItem
                                key={item.id}
                                checked={assignmentItineraryFilterIds.includes(item.id)}
                                onCheckedChange={(checked) => toggleAssignmentItineraryFilter(item.id, checked === true)}
                                onSelect={(event) => event.preventDefault()}
                              >
                                <span className="min-w-0">
                                  <span className="block truncate">{getDisplayText(item.name, 'Itinerary event')}</span>
                                  <span className="block text-xs text-muted-foreground">
                                    {formatDateOnly(item.date)}{isTaggedToHotel ? ' | Tagged to hotel' : ''}
                                  </span>
                                </span>
                              </DropdownMenuCheckboxItem>
                            );
                          })}
                          {assignmentItineraryOptions.length === 0 && (
                            <p className="px-2 py-1.5 text-sm text-muted-foreground">No itinerary events available.</p>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Guest side</Label>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="mt-1 h-9 w-full justify-between border-[#D8CBB9] bg-[#FBFAF8] px-3 text-sm"
                              data-testid={`allocation-side-filter-${assignmentSideFilter}`}
                            >
                              <span className="truncate">{assignmentSideLabel}</span>
                              <CaretDown className="h-3.5 w-3.5 shrink-0" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-44">
                            {[
                              { value: 'both', label: 'Both sides' },
                              { value: 'bride', label: 'Bride side' },
                              { value: 'groom', label: 'Groom side' }
                            ].map((option) => (
                              <DropdownMenuItem
                                key={option.value}
                                onSelect={() => {
                                  setAssignmentRoomNeedFilter((current) => current === 'show_all' ? 'all' : current);
                                  setAssignmentSideFilter(option.value);
                                }}
                              >
                                {option.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                  </div>
                  )}
              </div>

              {assignmentMode === 'uninvited' && showAdHocGuestForm && (
                <div className="rounded-xl border bg-muted/20 p-3">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-wedding-charcoal">Add New Guest</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setShowAdHocGuestForm(false)}
                      title="Close new guest form"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="adhoc-guest-name">Name *</Label>
                      <Input
                        id="adhoc-guest-name"
                        value={adHocGuest.name}
                        onChange={(event) => setAdHocGuest({ ...adHocGuest, name: event.target.value })}
                        placeholder="Guest name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="adhoc-guest-phone">Phone *</Label>
                      <Input
                        id="adhoc-guest-phone"
                        value={adHocGuest.phone}
                        onChange={(event) => setAdHocGuest({ ...adHocGuest, phone: event.target.value })}
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="adhoc-guest-email">Email</Label>
                      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                        <Input
                          id="adhoc-guest-email"
                          type="email"
                          value={adHocGuest.email}
                          onChange={(event) => setAdHocGuest({ ...adHocGuest, email: event.target.value })}
                          placeholder="Optional"
                        />
                        <Button
                          type="button"
                          className="bg-[#8B654E] hover:bg-[#7A5844]"
                          onClick={handleCreateAdHocGuestAndAssign}
                          disabled={creatingAdHocGuest}
                        >
                          <Plus className="mr-1 h-4 w-4" />
                          {creatingAdHocGuest ? 'Creating...' : 'Create and Assign'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-wedding-charcoal">Guest list</p>
                  <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    {selectedGuestIds.length > 0 && <span>{selectedBookingLabel}</span>}
                    <span>{filteredAssignmentGuests.length} shown</span>
                    {assignmentRoomNeedFilter !== 'show_all' && (
                      <button
                        type="button"
                        className="font-semibold text-[#8B654E] underline-offset-2 hover:underline"
                        onClick={handleShowAllAssignmentGuests}
                        data-testid="show-all-assignment-guests"
                      >
                        Show all guests
                      </button>
                    )}
                  </div>
                </div>
                <div className="relative">
                  <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={guestSearchQuery}
                    onChange={(e) => setGuestSearchQuery(e.target.value)}
                    placeholder="Search guest by name or phone"
                    className="h-10 border-[#D8CBB9] bg-white pl-9"
                    data-testid="assign-guest-select"
                  />
                </div>
                <div className="max-h-[280px] overflow-y-auto rounded-md border border-[#E8DED3] bg-white shadow-sm">
                {filteredAssignmentGuests.map((guest) => {
                  const isSelected = selectedGuestIds.includes(guest.id);
                  const occupantCount = getGuestOccupantCount(guest);
                  const allocatedBedCount = getGuestAllocatedBedsForSelection(guest);
                  const remainingBedCount = getGuestRemainingBedCount(guest);
                  const assignmentState = getGuestAssignmentState(guest);
                  const assignedRoomLabels = getGuestAssignedRoomLabels(guest);
                  const companionMembers = getGuestBookingMembers(guest).filter((member) => member.id !== guest.id);
                  const unnamedCompanionCount = Math.max(occupantCount - 1 - companionMembers.length, 0);
                  const companionLabels = [
                    ...companionMembers.map((member) => getDisplayText(member.name, 'Guest')),
                    unnamedCompanionCount > 0
                      ? `${unnamedCompanionCount} unnamed ${unnamedCompanionCount === 1 ? 'guest' : 'guests'}`
                      : ''
                  ].filter(Boolean);
                  const guestBookingEventLabel = getGuestBookingEventLabel(guest);
                  const assignmentGuestDisplay = getAssignmentGuestDisplay(guest);
                  return (
                    <div key={guest.id} className={`flex min-h-14 flex-col gap-2 border-b border-[#EEE7DF] px-3.5 py-3 transition-colors last:border-b-0 sm:flex-row sm:items-center sm:justify-between ${isSelected ? 'bg-[#FCF8F3] ring-1 ring-inset ring-[#E7D8C8]' : 'bg-white hover:bg-[#FBFAF8]'}`}>
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <Checkbox
                          id={`assign-guest-${guest.id}`}
                          checked={isSelected}
                          onCheckedChange={(checked) => handleToggleGuestSelection(guest.id, checked === true)}
                        />
                        <div className="min-w-0">
                          <Label htmlFor={`assign-guest-${guest.id}`} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold text-wedding-charcoal">
                            <span className="truncate">{assignmentGuestDisplay.name}</span>
                            <span className="shrink-0 text-xs font-normal tabular-nums text-muted-foreground">
                              {getDisplayText(guest.phone, 'No phone')}
                            </span>
                          </Label>
                          {assignmentGuestDisplay.detail && (
                            <p className="text-xs leading-snug text-[#8B654E]">
                              {assignmentGuestDisplay.detail}
                            </p>
                          )}
                          <p className="text-xs leading-snug text-muted-foreground">
                            {allocatedBedCount > 0
                              ? remainingBedCount > 0
                                ? `${remainingBedCount} of ${occupantCount} people still need beds | ${allocatedBedCount} placed`
                                : `${occupantCount} ${occupantCount === 1 ? 'person' : 'people'} placed`
                              : `${occupantCount} ${occupantCount === 1 ? 'person' : 'people'}`}
                            {companionLabels.length > 0 ? ` | ${companionLabels.join(', ')}` : ''}
                          </p>
                          <p className="text-xs leading-snug text-muted-foreground" title={`Events: ${guestBookingEventLabel}`}>
                            Events: {guestBookingEventLabel}
                          </p>
                          {assignedRoomLabels.length > 0 && (
                            <p className="truncate text-xs font-medium text-[#8B654E]">
                              Room assigned: {assignedRoomLabels.join(' | ')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-1 pl-8 sm:justify-end sm:pl-0">
                        {isSelected && (
                          <span className="rounded-full bg-[#8B654E]/10 px-2 py-0.5 text-[0.625rem] font-semibold text-[#8B654E]">
                            Selected
                          </span>
                        )}
                        {assignmentState === 'assigned' && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[0.625rem] font-semibold text-emerald-700">
                            Room assigned
                          </span>
                        )}
                        {assignmentState === 'partial' && (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[0.625rem] font-semibold text-amber-700">
                            Partially assigned
                          </span>
                        )}
                        {assignmentState === 'unassigned' && (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.625rem] font-semibold text-slate-600">
                            Room unassigned
                          </span>
                        )}
                        {!guestRequiresRoom(guest) && (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.625rem] font-semibold text-slate-600">
                            No room tag
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {filteredAssignmentGuests.length === 0 && (
                  <p className="p-4 text-sm text-muted-foreground">
                    {hasAssignmentGuestFilters
                      ? 'No guests match these filters.'
                      : 'No room-needing guests match this hotel.'}
                  </p>
                )}
                </div>
              </div>

              {assignableGuestBookings.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No available guest bookings for this room.
                </p>
              )}

              {selectedGuestIds.length > 0 && !allocationExceeded && (
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                  This room can accommodate the selected booking.
                </div>
              )}

              {allocationExceeded && (
                <div className="space-y-3">
                  <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm">
                    <WarningCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" weight="bold" />
                    <div>
                      <p className="font-semibold text-wedding-charcoal">Room is over capacity</p>
                      <p className="text-rose-700">
                        This booking needs {selectedPeopleRequired} beds, but this room has {selectedRoomAvailableBeds}.
                      </p>
                    </div>
                  </div>

                  {bestRoomRecommendation ? (
                    <div className="flex flex-col gap-3 rounded-xl border border-[#E7D8C8] bg-[#FCF8F3] px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-wedding-charcoal">Recommended room</p>
                        <p className="truncate text-sm text-wedding-charcoal">
                          {getDisplayText(bestRoomRecommendation.room.hotel_name)} | Room {getDisplayText(bestRoomRecommendation.room.room_number)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {bestRoomRecommendation.availableBeds} beds available{bestRoomRecommendation.unusedBeds === 0 ? ' | Perfect fit' : ''}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-[#8B654E] hover:bg-[#7A5844]"
                        onClick={() => {
                          setSelectedRoom(bestRoomRecommendation.room);
                          setAllowSplitOverride(false);
                        }}
                      >
                        Use Room {getDisplayText(bestRoomRecommendation.room.room_number)}
                      </Button>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-[#E7D8C8] bg-[#FCF8F3] px-3 py-3 text-sm">
                      <p className="font-semibold text-wedding-charcoal">No single room can accommodate this booking.</p>
                      {canSplitAcrossRooms ? (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs font-semibold text-[#8B654E]">Show split plan</summary>
                          <div className="mt-2 space-y-1.5">
                            {splitRoomSuggestions.map((item) => (
                              <div key={item.room.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-2">
                                <span className="font-medium text-wedding-charcoal">{getDisplayText(item.room.hotel_name)} | Room {getDisplayText(item.room.room_number)}</span>
                                <span className="text-xs text-muted-foreground">{item.availableBeds} beds</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      ) : (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Add more rooms or free up beds before assigning this booking.
                        </p>
                      )}
                    </div>
                  )}

                  {selectedHasCompanionGroup && (
                    <div className="flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                      <Info className="h-4 w-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">Continuing will split this companion group.</span>
                      <details className="shrink-0">
                        <summary className="cursor-pointer text-xs font-semibold text-blue-900">Details</summary>
                        <p className="mt-2 max-w-xs text-xs text-blue-800">
                          This booking contains more people than the current room can fit. Continue only if companions will be allocated separately.
                        </p>
                      </details>
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter className="shrink-0 flex flex-col gap-3 border-t bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <p className="text-xs text-muted-foreground">
                {selectedGuestIds.length > 0 ? selectedBookingLabel : `${remainingBedsAfterSelection} beds free`}
              </p>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    setShowAssignDialog(false);
                    setAssignmentStayPlanId('');
                  }}
                >
                  Cancel
                </Button>
                {allocationExceeded && bestRoomRecommendation && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleAssignGuest(true)}
                    disabled={selectedGuestIds.length === 0 || assigningGuests || !canSplitAcrossRooms}
                    className="w-full sm:w-auto"
                  >
                    {assigningGuests ? 'Assigning...' : 'Divide across rooms'}
                  </Button>
                )}
                <Button
                  onClick={allocationExceeded && bestRoomRecommendation
                    ? () => {
                        setSelectedRoom(bestRoomRecommendation.room);
                        setAllowSplitOverride(false);
                      }
                    : allocationExceeded
                      ? () => handleAssignGuest(true)
                      : handleAssignGuest}
                  disabled={selectedGuestIds.length === 0 || assigningGuests || (allocationExceeded && !bestRoomRecommendation && !canSplitAcrossRooms)}
                  className="h-auto min-h-10 w-full whitespace-normal bg-[#8B654E] px-3 text-center hover:bg-[#7A5844] sm:w-auto"
                  data-testid="confirm-assign-btn"
                >
                  {assigningGuests
                    ? 'Assigning...'
                    : allocationExceeded && bestRoomRecommendation
                      ? 'Use recommended room'
                      : allocationExceeded && canSplitAcrossRooms
                        ? 'Divide across rooms'
                        : allocationExceeded
                          ? 'Not enough room capacity'
                      : `Assign ${selectedGuestIds.length} Booking${selectedGuestIds.length > 1 ? 's' : ''}`}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
